/**
 * Lienzo del Creador: páginas, fuentes, widgets, guardar y publicar.
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './widget/creatorCanvas.css';
import WidgetRenderer from './widget/WidgetRenderer';
import WidgetConfigurator from './widget/WidgetConfigurator';
import ImageAssetField from './widget/ImageAssetField';
import NestedWidgetCanvas from './widget/NestedWidgetCanvas';
import DashboardFilterBar from './widget/DashboardFilterBar';
import FieldPalette from './widget/FieldPalette';
import CreatorStudioBar from './widget/CreatorStudioBar';
import DriveSpreadsheetPicker from './widget/DriveSpreadsheetPicker';
import ReplaceSourceDialog from './widget/ReplaceSourceDialog';
import { applyColumnMapping, collectColumnUsage, widgetColumnRefs, findMissingFields, changedSheetSourceIds, widgetSourceId } from './widget/sourceReplace';
import MissingFieldsBanner from './widget/MissingFieldsBanner';
import DataSourcesStudio from './widget/DataSourcesStudio';
import { getDriveAccessToken } from './widget/googleDriveClient';
import JoinMapModal from './widget/JoinMapModal';
import { guessJoinKeys, wouldCreateJoinCycle } from './widget/JoinBlendCanvas';
import CustomReportRenderer from '../CustomReportRenderer';
import ReportPageOutline from './widget/ReportPageOutline';
import { createReportSection, pageNavPayload, applyOutlineDrop, buildPageOutline } from './widget/pageNav';
import { widgetZ, layerWidgets, alignLayoutItems, canvasGridRows, layoutSignature } from './widget/arrange';
import { engineFetch, engineErrorMessage, clearQueryCache, bumpEngineDataVersion, refreshEngineSources, DEFAULT_CANVAS, INNER_CANVAS_COLUMNS, CANVAS_GRID_COLUMNS, CANVAS_RESIZE_HANDLES, RGL_BREAKPOINTS, rglCols, scaleLayout, createWidget, createLayoutItem, cloneWidget, cloneReportPage, createReportPage, normalizeReportPages, normalizeDataSources, applySourceIdToWidgets, createDataSource, createJoin, sourceLabel, sourceRequestFields, buildWidgetQueryPayload, widgetHasQueryShape, normalizeWidgetType, isDecorationType, isContainerType, parseSpreadsheetInput, findWidgetById, findParentWidget, findLayoutItem, updateWidgetById, removeWidgetById, addWidgetToContainer, collectFilterIds, stampDataSource, reflowLayout, containerHeightForInner, findSlotNear, getWidgetStyle, getTextContentStyle, getBlockTitleStyle, getBlockSubtitleStyle, getBlockFrameStyle, artboardSurface, pruneJoinGraph, remapWidgetsDataSource, normalizeSelectedColumns } from './widget/engineConfig';
import { buildEngineFilters } from './widget/filterUtils';
import { DashboardThemeContext, REPORT_THEMES, NEW_REPORT_THEME, normalizeReportTheme } from './widget/dashboardTheme';
import { setCreatorDirtyCheck } from './widget/leaveGuard';

import { applyFieldToWidget, removeCalculatedFromWidgets, updateCalculatedInWidgets, preventDragOver } from './widget/fieldActions';
import { studio } from './studioUi';
import { useAuth } from '../../AuthContext';

const ResponsiveGridLayout = WidthProvider(Responsive);

/** Atajos del Creador (se muestran con «?»). */
const CREATOR_SHORTCUTS = [
    ['Ctrl + S', 'Guardar (mantiene borrador o publicado)'],
    ['Ctrl + Z', 'Deshacer'],
    ['Ctrl + Shift + Z · Ctrl + Y', 'Rehacer'],
    ['Ctrl + D', 'Duplicar el elemento seleccionado'],
    ['Supr', 'Eliminar el elemento seleccionado'],
    ['Flechas (Shift ×4)', 'Mover la selección'],
    ['Ctrl + ] · Ctrl + [', 'Adelante / atrás (con Shift: al frente / al fondo)'],
    ['Ctrl + clic', 'Seleccionar varios'],
    ['Esc', 'Cerrar menús o la vista previa'],
    ['?', 'Mostrar u ocultar esta ayuda'],
];

/** Alto mínimo (filas) de un KPI con comparación, meta o tendencia para que se vean. */
const KPI_EXTRAS_MIN_ROWS = 11;

const AUTOSAVE_MINUTES_KEY = 'creator-autosave-minutes';
const AUTOSAVE_ENABLED_KEY = 'creator-autosave-enabled';
const AUTOSAVE_CHOICES = [1, 2, 5, 10, 15, 30];

function readAutoSaveMinutes() {
    try {
        const n = Number(window.localStorage.getItem(AUTOSAVE_MINUTES_KEY));
        if (AUTOSAVE_CHOICES.includes(n)) return n;
    } catch (err) { /* ignore */ }
    return 5;
}

function readAutoSaveEnabled() {
    try {
        const raw = window.localStorage.getItem(AUTOSAVE_ENABLED_KEY);
        if (raw === '0' || raw === 'false') return false;
    } catch (err) { /* ignore */ }
    return true;
}

/**
 * Editor del Creador. Lo monta ReportCreatorShell: `initialReport` (o un reporte nuevo con
 * `initialPreset`) llega al montar, y abrir otro reporte monta un editor nuevo.
 * Sin `initialCapabilities.canEdit` el reporte se abre en solo lectura: no se guarda.
 */
const ReportCreatorWorkspace = ({
    styles,
    fetchWithAuth,
    API_URL,
    clients,
    projects,
    refreshAllData,
    libraryReports = [],
    initialReport = null,
    initialCapabilities = null,
    initialPreset = null,
    onGoHome,
    onOpenReport,
    onNewReport,
    onSaved,
}) => {
    const { withBusy } = useAuth();
    const [dataSources, setDataSources] = useState([]);
    const [joins, setJoins] = useState([]);
    const [activeSourceId, setActiveSourceId] = useState('');
    const [schemasBySource, setSchemasBySource] = useState({});
    const [sheetsBySource, setSheetsBySource] = useState({});
    const [loadingColumns, setLoadingColumns] = useState(false);
    const [loadingSheets, setLoadingSheets] = useState(false);
    const [refreshingSources, setRefreshingSources] = useState(false);
    const [drivePickerOpen, setDrivePickerOpen] = useState(false);
    const [joinMapOpen, setJoinMapOpen] = useState(false);
    const [calculatedFields, setCalculatedFields] = useState([]);
    const [draggingField, setDraggingField] = useState(null);

    const [widgets, setWidgets] = useState([]);
    const [layout, setLayout] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const layoutRef = useRef([]);
    const canvasRef = useRef(null);
    const extractLockRef = useRef(false);
    const sourceFilterTimer = useRef(null);
    const [extractingFrom, setExtractingFrom] = useState(null);

    const [filterDefs, setFilterDefs] = useState([]);
    const [filterValues, setFilterValues] = useState({});
    const [canvas, setCanvas] = useState({ ...DEFAULT_CANVAS });
    // Tema del reporte completo: los nuevos nacen «Ejecutivo»; los guardados sin tema son «Clásico».
    const [reportTheme, setReportTheme] = useState(NEW_REPORT_THEME);
    const [pages, setPages] = useState(() => [createReportPage('Hoja 1', { id: 'page-default' })]);
    const [sections, setSections] = useState([]);
    const [activePageId, setActivePageId] = useState('page-default');
    const [pageOutlineOpen, setPageOutlineOpen] = useState(true);

    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [reportNameInput, setReportNameInput] = useState('');
    const [savingReport, setSavingReport] = useState(false);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(readAutoSaveEnabled);
    const [autoSaveMinutes, setAutoSaveMinutes] = useState(readAutoSaveMinutes);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [autoSaving, setAutoSaving] = useState(false);
    const saveLockRef = useRef(false);
    const savedSnapshotRef = useRef('');
    const getSnapshotRef = useRef(() => '');
    const saveReportRef = useRef(async () => {});
    const autoSaveCtxRef = useRef({ editingReportId: '' });
    // Estado de publicación del reporte abierto: el autoguardado no publica un borrador.
    const isDraftRef = useRef(initialReport ? initialReport.isDraft !== false : true);
    const readOnly = Boolean(initialReport && initialCapabilities && !initialCapabilities.canEdit);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(true);
    const [propsOpen, setPropsOpen] = useState(true);
    const [canvasOpen, setCanvasOpen] = useState(false);
    const [elementOpen, setElementOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const redoRef = useRef([]);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    // Cambia cada vez que se toma una nueva línea base de «guardado» (para recalcular el estado).
    const [, setSavedVersion] = useState(0);
    const dirtyRef = useRef(false);
    // Mientras se abre un reporte (fuentes y esquemas cargando) no cuenta como «sin guardar».
    const loadingBaselineRef = useRef(false);
    const historyRef = useRef([]);
    const [editingReportId, setEditingReportId] = useState('');
    const [error, setError] = useState(null);
    const [sourceNotice, setSourceNotice] = useState(null);
    /* Ventana «Reemplazar fuente»: { mode, sourceId, widgetId?, targetSourceId?, initialInput?, previousInput? } */
    const [replaceRequest, setReplaceRequest] = useState(null);
    const customReports = libraryReports;
    autoSaveCtxRef.current = { editingReportId };

    const selectedWidget = findWidgetById(widgets, selectedId);
    const activeSource = dataSources.find(item => item.id === activeSourceId) || dataSources[0] || null;
    const spreadsheetId = activeSource?.spreadsheet_id || '';
    const spreadsheetInput = activeSource?.input || '';
    const sheetName = activeSource?.sheet_name || '';
    const availableSheets = sheetsBySource[activeSource?.id] || [];
    const paletteSourceId = activeSource?.id || '';
    const schemaColumns = schemasBySource[paletteSourceId]?.columns || [];
    const rowCount = schemasBySource[activeSource?.id]?.rowCount ?? null;
    const sheetSources = dataSources.filter(item => item.kind !== 'join');
    const editorColumns = schemasBySource[selectedWidget?.query?.dataSourceId || paletteSourceId]?.columns || schemaColumns;
    const knownNames = useMemo(
        () => [...editorColumns.map(col => col.name), ...calculatedFields.map(field => field.name)],
        [editorColumns, calculatedFields]
    );
    const engineFilters = useMemo(
        () => buildEngineFilters(filterDefs, filterValues),
        [filterDefs, filterValues]
    );

    const flushedPages = useMemo(
        () => pages.map(page => page.id === activePageId
            ? { ...page, widgets, layout, canvas, filters: filterDefs }
            : page),
        [pages, activePageId, widgets, layout, canvas, filterDefs]
    );

    const previewReport = useMemo(() => ({
        reporteNombre: reportNameInput.trim() || 'Vista previa',
        customConfig: JSON.stringify({
            version: 5,
            dataSource: {
                spreadsheet_id: sheetSources[0]?.spreadsheet_id || spreadsheetId,
                sheet_name: sheetSources[0]?.sheet_name || sheetName
            },
            dataSources,
            joins,
            calculatedFields,
            filters: filterDefs,
            pages: flushedPages,
            sections,
            activePageId,
            theme: reportTheme
        })
    }), [reportNameInput, spreadsheetId, sheetName, dataSources, joins, calculatedFields, filterDefs, flushedPages, sections, activePageId, reportTheme]);

    getSnapshotRef.current = () => JSON.stringify({
        dataSources,
        joins,
        calculatedFields,
        pages: flushedPages,
        theme: reportTheme,
        sections,
        reportNameInput,
        selectedClientId,
        selectedProjectId
    });

    const applyPage = (page) => {
        setActivePageId(page.id);
        setWidgets(page.widgets || []);
        setLayout(page.layout || []);
        setCanvas({ ...DEFAULT_CANVAS, ...(page.canvas || {}) });
        setFilterDefs(page.filters || []);
        setSelectedId(null);
        setSelectedIds([]);
    };

    useEffect(() => {
        setPages(prev => {
            const current = prev.find(page => page.id === activePageId);
            if (!current) return prev;
            const same = current.widgets === widgets && current.layout === layout && current.canvas === canvas && current.filters === filterDefs;
            if (same) return prev;
            return prev.map(page => page.id === activePageId ? { ...page, widgets, layout, canvas, filters: filterDefs } : page);
        });
    }, [widgets, layout, canvas, filterDefs, activePageId]);

    /** Foto del estado editable (para deshacer y rehacer). */
    const snapshotState = () => ({
        widgets: JSON.parse(JSON.stringify(widgets)),
        layout: JSON.parse(JSON.stringify(layout)),
        canvas: JSON.parse(JSON.stringify(canvas)),
        pages: JSON.parse(JSON.stringify(flushedPages)),
        sections: JSON.parse(JSON.stringify(sections)),
        activePageId,
        filterDefs: JSON.parse(JSON.stringify(filterDefs)),
        dataSources: JSON.parse(JSON.stringify(dataSources)),
        joins: JSON.parse(JSON.stringify(joins)),
        calculatedFields: JSON.parse(JSON.stringify(calculatedFields)),
        selectedId,
        selectedIds: [...selectedIds]
    });

    const captureHistory = () => {
        historyRef.current = [...historyRef.current, snapshotState()].slice(-20);
        setCanUndo(true);
        // Un cambio nuevo invalida lo que se podía rehacer.
        redoRef.current = [];
        setCanRedo(false);
    };

    const handleUndo = () => {
        const prev = historyRef.current.pop();
        if (!prev) {
            setCanUndo(false);
            return;
        }
        redoRef.current = [...redoRef.current, snapshotState()].slice(-20);
        setCanRedo(true);
        restoreState(prev);
        setCanUndo(historyRef.current.length > 0);
    };

    const handleRedo = () => {
        const next = redoRef.current.pop();
        if (!next) {
            setCanRedo(false);
            return;
        }
        historyRef.current = [...historyRef.current, snapshotState()].slice(-20);
        setCanUndo(true);
        restoreState(next);
        setCanRedo(redoRef.current.length > 0);
    };

    const restoreState = (prev) => {
        setPages(prev.pages || [createReportPage('Hoja 1', { id: 'page-default' })]);
        setSections(prev.sections || []);
        setActivePageId(prev.activePageId || prev.pages?.[0]?.id || 'page-default');
        setWidgets(prev.widgets || []);
        setLayout(prev.layout || []);
        setCanvas({ ...DEFAULT_CANVAS, ...(prev.canvas || {}) });
        setFilterDefs(prev.filterDefs || []);
        /* Fotos de antes de guardar fuentes y calculados no los traen: se dejan como están. */
        if (prev.dataSources) {
            /* Si deshacer regresa una fuente a otro archivo o pestaña, se releen sus columnas. */
            const changed = changedSheetSourceIds(dataSources, prev.dataSources);
            setDataSources(prev.dataSources);
            changed.forEach(sourceId => handleDiscoverColumns({
                sourceId,
                dataSources: prev.dataSources,
                joins: prev.joins || joins,
                refresh: false,
            }));
        }
        if (prev.joins) setJoins(prev.joins);
        if (prev.calculatedFields) setCalculatedFields(prev.calculatedFields);
        setSelectedId(prev.selectedId || null);
        setSelectedIds(prev.selectedIds || []);
    };

    const handleSelectPage = (pageId) => {
        if (pageId === activePageId) return;
        const next = flushedPages.find(page => page.id === pageId);
        if (!next) return;
        setPages(flushedPages);
        applyPage(next);
    };

    const handleAddPage = (sectionIdArg) => {
        captureHistory();
        const current = flushedPages.find(page => page.id === activePageId);
        const explicit = typeof sectionIdArg === 'string' || sectionIdArg === null;
        const sectionId = explicit ? sectionIdArg : (current?.sectionId || null);
        const page = createReportPage(`Hoja ${flushedPages.length + 1}`, { sectionId });
        const nextPages = [...flushedPages];
        let insertAt = nextPages.findIndex(item => item.id === activePageId);
        insertAt = insertAt >= 0 ? insertAt + 1 : nextPages.length;
        if (explicit && sectionId) {
            const lastInSection = [...nextPages].map((item, idx) => (item.sectionId === sectionId ? idx : -1)).filter(idx => idx >= 0).pop();
            insertAt = lastInSection >= 0 ? lastInSection + 1 : nextPages.length;
        }
        nextPages.splice(insertAt, 0, page);
        setPages(nextPages);
        applyPage(page);
        setPageOutlineOpen(true);
    };

    const handleAddSection = () => {
        captureHistory();
        const section = createReportSection(`Sección ${sections.length + 1}`);
        setSections(prev => [...prev, section]);
        setPageOutlineOpen(true);
        setSourceNotice('Sección creada. Arrastra una o más hojas dentro para agruparlas, o pulsa + en la sección.');
        setError(null);
    };

    const handlePatchPage = (pageId, partial) => {
        setPages(prev => {
            const mapped = prev.map(page => page.id === pageId ? { ...page, ...partial } : page);
            if (!Object.prototype.hasOwnProperty.call(partial, 'sectionId')) return mapped;
            const from = mapped.findIndex(page => page.id === pageId);
            if (from < 0) return mapped;
            const next = [...mapped];
            const [item] = next.splice(from, 1);
            if (!item.sectionId) {
                next.splice(from, 0, item);
                return next;
            }
            let last = -1;
            next.forEach((page, idx) => {
                if (page.sectionId === item.sectionId) last = idx;
            });
            next.splice(last >= 0 ? last + 1 : next.length, 0, item);
            return next;
        });
    };

    const handlePatchSection = (sectionId, partial) => {
        setSections(prev => prev.map(section => section.id === sectionId ? { ...section, ...partial } : section));
    };

    const handleRenameSection = (sectionId, name) => {
        handlePatchSection(sectionId, { name });
    };

    const handleDeleteSection = (sectionId) => {
        const target = sections.find(section => section.id === sectionId);
        if (!window.confirm(`¿Quitar la sección "${target?.name || ''}"? Las hojas se conservan.`)) return;
        captureHistory();
        setSections(prev => prev.filter(section => section.id !== sectionId));
        setPages(prev => prev.map(page => page.sectionId === sectionId ? { ...page, sectionId: null } : page));
    };

    const handleOutlineDrop = (from, to) => {
        const result = applyOutlineDrop(flushedPages, sections, from, to);
        if (!result) return;
        captureHistory();
        setPages(result.pages);
        setSections(result.sections);
    };

    const handleMoveSection = (sectionId, delta) => {
        const nodes = buildPageOutline(flushedPages, sections);
        const index = nodes.findIndex(node => node.type === 'section' && node.section.id === sectionId);
        const target = nodes[index + delta];
        if (index < 0 || !target) return;
        handleOutlineDrop(
            { type: 'section', id: sectionId },
            {
                type: target.type,
                id: target.type === 'section' ? target.section.id : target.page.id,
                place: delta > 0 ? 'after' : 'before',
            }
        );
    };

    const handleMovePage = (pageId, delta) => {
        setPages(prev => {
            const index = prev.findIndex(page => page.id === pageId);
            const nextIndex = index + delta;
            if (index < 0 || nextIndex < 0 || nextIndex >= prev.length) return prev;
            const next = [...prev];
            const [item] = next.splice(index, 1);
            next.splice(nextIndex, 0, item);
            return next;
        });
    };

    const handleDuplicatePage = () => {
        captureHistory();
        const source = flushedPages.find(page => page.id === activePageId) || flushedPages[0];
        const copy = cloneReportPage(source, `${source.name} (copia)`);
        const index = flushedPages.findIndex(page => page.id === source.id);
        const nextPages = [...flushedPages];
        nextPages.splice(index + 1, 0, copy);
        setPages(nextPages);
        applyPage(copy);
    };

    const handleDeletePage = (pageId) => {
        if (flushedPages.length <= 1) return;
        const target = flushedPages.find(page => page.id === pageId);
        if (!window.confirm(`¿Eliminar la hoja "${target?.name || ''}"?`)) return;
        captureHistory();
        const remaining = flushedPages.filter(page => page.id !== pageId);
        const nextIndex = Math.min(
            flushedPages.findIndex(page => page.id === pageId),
            remaining.length - 1
        );
        setPages(remaining);
        applyPage(remaining[nextIndex]);
    };

    const handleRenamePage = (pageId, name) => {
        setPages(prev => prev.map(page => page.id === pageId ? { ...page, name } : page));
    };

    const toggleMenu = (key) => setOpenMenu(current => (current === key ? null : key));

    const assignField = (widget, field, slot = 'auto') => {
        if (!widget || !field) return;
        const next = applyFieldToWidget(widget, field, slot, knownNames);
        if (next === widget && field.dataSourceId && widget.query?.dataSourceId && field.dataSourceId !== widget.query.dataSourceId) {
            /* Campo de otra fuente sobre un elemento con datos: se ofrece cambiarlo de fuente. */
            openReplaceDialog({ mode: 'widget', widgetId: widget.id, sourceId: widgetSourceId(widget, dataSources), targetSourceId: field.dataSourceId });
            return;
        }
        captureHistory();
        setWidgets(prev => updateWidgetById(prev, widget.id, () => next));
        setSelectedId(widget.id);
        if (next.query?.dataSourceId) setActiveSourceId(next.query.dataSourceId);
        if (next.type === 'slicer' && next.filterId) {
            const col = next.query?.dimensions?.[0];
            if (!col) return;
            const colMeta = schemaColumns.find(item => item.name === col);
            const def = {
                id: next.filterId,
                column: col,
                label: next.title || col,
                type: 'multiselect',
                options: colMeta?.values || [],
                dataSourceId: next.query?.dataSourceId || field.dataSourceId || paletteSourceId
            };
            setFilterDefs(prev => (
                prev.some(item => item.id === next.filterId)
                    ? prev.map(item => item.id === next.filterId ? { ...item, ...def } : item)
                    : [...prev, def]
            ));
        }
    };

    const patchSource = (id, partial) => {
        setDataSources(prev => prev.map(item => item.id === id ? { ...item, ...partial } : item));
    };

    const handleSourceFiltersChange = (nextFilters) => {
        if (!activeSource) return;
        const catalog = dataSources.map(item => (
            item.id === activeSource.id ? { ...item, filters: nextFilters } : item
        ));
        setDataSources(catalog);
        if (sourceFilterTimer.current) clearTimeout(sourceFilterTimer.current);
        const ready = activeSource.kind === 'join'
            ? true
            : Boolean(activeSource.spreadsheet_id && activeSource.sheet_name);
        if (!ready) return;
        sourceFilterTimer.current = setTimeout(() => {
            handleDiscoverColumns({ sourceId: activeSource.id, dataSources: catalog });
        }, 400);
    };

    const handleSourceColumnsChange = (sourceId, nextSelection) => {
        const id = sourceId || activeSource?.id;
        if (!id) return;
        const selectedColumns = normalizeSelectedColumns(nextSelection);
        setDataSources(prev => prev.map(item => (
            item.id === id ? { ...item, selectedColumns } : item
        )));
    };

    const applyCatalogPrune = (pruned, notice) => {
        const fallback = pruned.dataSources.find(item => item.kind !== 'join') || pruned.dataSources[0] || {};
        setJoins(pruned.joins);
        setDataSources(pruned.dataSources);
        setWidgets(prev => remapWidgetsDataSource(prev, pruned.removedIds, fallback));
        setPages(prev => prev.map(page => ({
            ...page,
            widgets: remapWidgetsDataSource(page.widgets || [], pruned.removedIds, fallback)
        })));
        setSchemasBySource(prev => {
            const next = { ...prev };
            pruned.removedIds.forEach((id) => { delete next[id]; });
            return next;
        });
        setSheetsBySource(prev => {
            const next = { ...prev };
            pruned.removedIds.forEach((id) => { delete next[id]; });
            return next;
        });
        if (pruned.removedIds.has(activeSourceId)) {
            setActiveSourceId(pruned.dataSources[0]?.id || '');
        }
        setError(null);
        if (notice) setSourceNotice(notice);
    };

    const handleRemoveJoin = (joinId) => {
        const joinSource = dataSources.find(item => item.joinId === joinId);
        const startId = joinSource?.id;
        if (!joinId) return;
        const used = widgets.some(widget => widget.query?.dataSourceId === startId)
            || joins.some(join => join.id !== joinId && (join.leftDataSourceId === startId || join.rightDataSourceId === startId));
        if (used && !window.confirm('Esta unión está en uso. ¿Eliminarla y las uniones que dependen de ella?')) return;
        const pruned = pruneJoinGraph(
            dataSources.filter(item => item.joinId !== joinId && item.id !== startId),
            joins.filter(join => join.id !== joinId),
            startId ? [startId] : []
        );
        if (startId) pruned.removedIds.add(startId);
        applyCatalogPrune(pruned, 'Unión eliminada.');
    };

    const handleClearJoins = () => {
        if (!joins.length && !dataSources.some(item => item.kind === 'join')) return;
        if (!window.confirm('¿Quitar todas las uniones? Las hojas se quedan; los gráficos que usaban una unión pasarán a la primera hoja.')) return;
        const joinSourceIds = dataSources.filter(item => item.kind === 'join').map(item => item.id);
        applyCatalogPrune({
            dataSources: dataSources.filter(item => item.kind !== 'join'),
            joins: [],
            removedIds: new Set(joinSourceIds),
        }, 'Uniones eliminadas. Quedan solo las hojas.');
    };

    const handleDiscoverColumns = async (override = {}) => {
        const sourceId = override.sourceId || activeSource?.id;
        const catalogSources = override.dataSources || dataSources;
        const catalogJoins = override.joins || joins;
        const source = catalogSources.find(item => item.id === sourceId) || activeSource;
        const sid = override.spreadsheet_id ?? source?.spreadsheet_id;
        const sname = override.sheet_name ?? source?.sheet_name;
        if (!sourceId) return;
        if (source?.kind !== 'join' && (!sid || !sname)) {
            setError('Pega la URL o el ID del spreadsheet y selecciona una hoja.');
            return;
        }
        setLoadingColumns(true);
        setError(null);
        try {
            const response = await engineFetch('/api/engine/schema', {
                method: 'POST',
                body: JSON.stringify({
                    ...sourceRequestFields({
                        ...source,
                        dataSourceId: sourceId,
                        spreadsheet_id: sid,
                        sheet_name: sname
                    }, { dataSources: catalogSources, joins: catalogJoins }, { includeSelection: false }),
                    refresh: override.refresh !== false
                })
            });
            if (!response.ok) {
                throw new Error(await engineErrorMessage(response, 'Error al conectar con la hoja.'));
            }
            const res = await response.json();
            if (!res.success || !res.columns?.length) {
                throw new Error('No se encontraron columnas legibles en la hoja.');
            }
            // La hoja se acaba de releer: los resultados guardados en el cliente ya no valen.
            if (override.refresh !== false) clearQueryCache();
            setSchemasBySource(prev => ({ ...prev, [sourceId]: { columns: res.columns, rowCount: res.row_count } }));
            setWidgets(prev => prev.map(widget => {
                if (widget.query?.dataSourceId && widget.query.dataSourceId !== sourceId) return widget;
                if (!widget.query?.dataSourceId && dataSources.length > 1) return widget;
                return stampDataSource(widget, { id: sourceId, spreadsheet_id: sid || '', sheet_name: sname || '' });
            }));
        } catch (err) {
            setError(err.message);
            setSchemasBySource(prev => ({ ...prev, [sourceId]: { columns: [], rowCount: null } }));
        } finally {
            setLoadingColumns(false);
        }
    };

    const handleLoadWorksheets = async (rawInput, preferredSheet, sourceIdArg, options = {}) => {
        const source = rawInput ?? spreadsheetInput;
        const parsed = parseSpreadsheetInput(source);
        const silent = Boolean(options.silent);
        if (!parsed.spreadsheet_id) {
            if (!silent) setError('Pega la URL completa de Google Sheets o el ID del spreadsheet.');
            return options.catalog || dataSources;
        }
        let targetId = sourceIdArg || activeSource?.id;
        let catalog = options.catalog || dataSources;
        const catalogJoins = options.joins || joins;
        if (!targetId) {
            const created = createDataSource({
                label: 'Fuente 1',
                spreadsheet_id: parsed.spreadsheet_id,
                input: source
            });
            catalog = [...catalog, created];
            setDataSources(catalog);
            setActiveSourceId(created.id);
            targetId = created.id;
        }
        setLoadingSheets(true);
        setError(null);
        try {
            const requestSheets = async () => engineFetch('/api/engine/worksheets', {
                method: 'POST',
                body: JSON.stringify({
                    url: source,
                    spreadsheet_id: parsed.spreadsheet_id,
                    // Al abrir un reporte basta la caché del motor (valida por modifiedTime).
                    refresh: options.refresh !== false
                })
            });
            let response = await requestSheets();
            if (!response.ok && response.status === 403 && !options.retriedDrive) {
                try {
                    await getDriveAccessToken('', { force: true });
                    response = await requestSheets();
                } catch (_err) {
                    /* se muestra el error original del motor */
                }
            }
            if (!response.ok) {
                throw new Error(await engineErrorMessage(response, 'Error al listar las hojas del spreadsheet.'));
            }
            const res = await response.json();
            const sheets = res.sheets || [];
            if (!sheets.length) {
                throw new Error('El spreadsheet no tiene pestañas visibles.');
            }
            const sid = res.spreadsheet_id || parsed.spreadsheet_id;
            const currentName = catalog.find(item => item.id === targetId)?.sheet_name;
            const nextSheet = preferredSheet
                || (sheets.some(item => item.title === currentName) ? currentName : null)
                || res.suggested_sheet
                || sheets[0].title;
            const patched = catalog.map(item => item.id === targetId ? {
                ...item,
                spreadsheet_id: sid,
                sheet_name: nextSheet,
                input: source,
                label: item.label || nextSheet
            } : item);
            // Solo se actualiza esta fuente: varias pueden cargarse en paralelo al abrir un reporte.
            const nextItem = patched.find(item => item.id === targetId);
            setDataSources(prev => (prev.some(item => item.id === targetId)
                ? prev.map(item => (item.id === targetId ? { ...item, ...nextItem } : item))
                : [...prev, nextItem]));
            setSheetsBySource(prev => ({ ...prev, [targetId]: sheets }));
            await handleDiscoverColumns({
                sourceId: targetId,
                spreadsheet_id: sid,
                sheet_name: nextSheet,
                dataSources: patched,
                joins: catalogJoins,
                refresh: options.refresh
            });
            return patched;
        } catch (err) {
            setError(err.message);
            if (targetId) setSheetsBySource(prev => ({ ...prev, [targetId]: [] }));
            return catalog;
        } finally {
            setLoadingSheets(false);
        }
    };

    /* ---------------- Reemplazar fuente ---------------- */

    const replaceReport = useMemo(
        () => ({ pages: flushedPages, calculatedFields, dataSources, joins }),
        [flushedPages, calculatedFields, dataSources, joins]
    );

    /* Elementos y filtros con columnas que ya no están en su fuente (aviso y etiqueta «Campo faltante»). */
    const missingItems = useMemo(
        () => findMissingFields(replaceReport, schemasBySource, calculatedFields.map(item => item.name)),
        [replaceReport, schemasBySource, calculatedFields]
    );
    const missingById = useMemo(
        () => Object.fromEntries(missingItems.filter(item => item.kind === 'widget').map(item => [item.id, item.fields])),
        [missingItems]
    );

    const goToMissing = (pageId, id, kind) => {
        const page = flushedPages.find(item => item.id === pageId);
        if (page && pageId !== activePageId) applyPage(page);
        if (kind === 'widget') setSelectedId(id);
    };

    /** ¿Algún elemento, filtro o unión usa la fuente? Entonces sus cambios pasan por la ventana. */
    const sourceInUse = (sourceId) => Boolean(sourceId)
        && Object.keys(collectColumnUsage(replaceReport, sourceId)).length > 0;

    const findReportWidget = (id) => {
        let hit = null;
        const walk = list => (list || []).forEach((item) => {
            if (hit) return;
            if (item.id === id) hit = item;
            else if (item.children?.widgets) walk(item.children.widgets);
        });
        flushedPages.forEach(page => walk(page.widgets));
        return hit;
    };

    function openReplaceDialog(request) {
        const source = dataSources.find(item => item.id === request.sourceId);
        setReplaceRequest({ ...request, previousInput: source?.input });
    }

    const replaceDialogProps = useMemo(() => {
        if (!replaceRequest) return null;
        const { mode, sourceId, widgetId, targetSourceId } = replaceRequest;
        const schemaCols = schemasBySource[sourceId]?.columns || [];
        const names = schemaCols.map(col => col.name);
        const typeOf = name => schemaCols.find(col => col.name === name)?.inferred_type;
        let usage = collectColumnUsage(replaceReport, sourceId, names);
        let used = Object.keys(usage);
        if (mode === 'widget') {
            const widget = findReportWidget(widgetId);
            const calcs = calculatedFields.map(item => item.name);
            used = widget ? widgetColumnRefs(widget, [...names, ...calcs]).filter(name => !calcs.includes(name)) : [];
            usage = Object.fromEntries(used.map(name => [name, (usage[name] || []).filter(item => item.id === widgetId)]));
        }
        return {
            mode,
            source: dataSources.find(item => item.id === sourceId),
            targetSource: targetSourceId ? dataSources.find(item => item.id === targetSourceId) : null,
            usedColumns: used.map(name => ({ name, inferred_type: typeOf(name) })),
            usage,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replaceRequest, schemasBySource, replaceReport]);

    const closeReplaceDialog = () => {
        /* Un enlace escrito en Fuentes que no se aplicó vuelve al que estaba. */
        if (replaceRequest?.previousInput !== undefined && replaceRequest.mode === 'source') {
            patchSource(replaceRequest.sourceId, { input: replaceRequest.previousInput });
        }
        setReplaceRequest(null);
    };

    const handleApplyReplace = async ({ spreadsheet_id, sheet_name, input, mapping, missing }) => {
        const request = replaceRequest;
        if (!request) return;
        captureHistory();
        const source = dataSources.find(item => item.id === request.sourceId);
        const fileChanged = request.mode === 'source'
            && (spreadsheet_id !== source?.spreadsheet_id || sheet_name !== source?.sheet_name);
        const next = applyColumnMapping(replaceReport, {
            sourceId: request.sourceId,
            mapping,
            widgetId: request.mode === 'widget' ? request.widgetId : undefined,
            targetSourceId: request.targetSourceId,
            sourcePatch: fileChanged ? { spreadsheet_id, sheet_name, input } : undefined,
        });
        const active = next.pages.find(page => page.id === activePageId) || next.pages[0];
        setPages(next.pages);
        setWidgets(active?.widgets || []);
        setFilterDefs(active?.filters || []);
        setCalculatedFields(next.calculatedFields);
        setDataSources(next.dataSources);
        setJoins(next.joins);
        clearQueryCache();
        setReplaceRequest(null);
        setError(null);
        setSourceNotice(missing.length
            ? `Fuente actualizada. ${missing.length} ${missing.length === 1 ? 'campo quedó' : 'campos quedaron'} sin equivalente: revisa el aviso de campos faltantes.`
            : 'Fuente actualizada sin perder elementos.');
        if (request.mode === 'widget') {
            setSelectedId(request.widgetId);
            if (request.targetSourceId && !schemasBySource[request.targetSourceId]?.columns?.length) {
                await handleDiscoverColumns({ sourceId: request.targetSourceId, dataSources: next.dataSources, joins: next.joins, refresh: false });
            }
            return;
        }
        if (fileChanged) {
            await handleLoadWorksheets(input || spreadsheet_id, sheet_name, request.sourceId, { catalog: next.dataSources, joins: next.joins, silent: true });
        }
    };

    const handleSheetChange = async (nextName) => {
        if (!activeSource?.id || !nextName) return;
        patchSource(activeSource.id, { sheet_name: nextName, label: activeSource.label || nextName });
        if (!spreadsheetId) return;
        await handleDiscoverColumns({
            sourceId: activeSource.id,
            spreadsheet_id: spreadsheetId,
            sheet_name: nextName
        });
    };

    /**
     * Vuelve a cargar pestañas y columnas de todas las fuentes (primero las hojas, luego
     * las uniones). Devuelve el catálogo con los datos que respondió el motor.
     */
    const loadSourceCatalog = async (catalogSources, catalogJoins, refresh) => {
        const sheetItems = catalogSources.filter(src => src.kind !== 'join' && src.spreadsheet_id);
        const loaded = await Promise.all(sheetItems.map(item => handleLoadWorksheets(
            item.input || item.spreadsheet_id,
            item.sheet_name,
            item.id,
            { catalog: catalogSources, joins: catalogJoins, silent: true, refresh }
        )));
        const catalog = catalogSources.map((src) => {
            const idx = sheetItems.findIndex(item => item.id === src.id);
            return idx >= 0 ? ((loaded[idx] || []).find(item => item.id === src.id) || src) : src;
        });
        await Promise.all(catalogSources.filter(src => src.kind === 'join').map(item => handleDiscoverColumns({
            sourceId: item.id,
            dataSources: catalog,
            joins: catalogJoins,
            refresh
        })));
        return catalog;
    };

    /** «Actualizar datos de Drive»: relee todas las hojas conectadas y repinta los widgets. */
    const handleRefreshAllSources = async () => {
        if (refreshingSources || !sheetSources.some(src => src.spreadsheet_id)) return;
        setRefreshingSources(true);
        setError(null);
        try {
            // El motor descarta su copia una vez; la recarga ya baja cada hoja fresca.
            await refreshEngineSources(dataSources);
            await loadSourceCatalog(dataSources, joins, false);
            clearQueryCache();
            bumpEngineDataVersion();
        } catch (err) {
            setError(err.message || 'No se pudieron actualizar las hojas de datos.');
        } finally {
            setRefreshingSources(false);
        }
    };

    const handleAddSource = () => {
        const created = createDataSource({ label: `Fuente ${sheetSources.length + 1}` });
        setDataSources(prev => [...prev, created]);
        setActiveSourceId(created.id);
        setOpenMenu('source');
    };

    const openAddData = () => {
        if (!dataSources.length) {
            handleAddSource();
            return;
        }
        setOpenMenu(current => (current === 'source' ? null : 'source'));
    };

    const openDrivePicker = () => {
        if (!dataSources.length || !activeSource || activeSource.kind === 'join') {
            handleAddSource();
        } else {
            setOpenMenu('source');
        }
        setDrivePickerOpen(true);
    };

    const handlePickDriveFile = (file) => {
        if (!file?.id) return;
        const url = file.url || `https://docs.google.com/spreadsheets/d/${file.id}`;
        const already = dataSources.find(item => item.kind !== 'join' && item.spreadsheet_id === file.id);
        if (already) {
            setActiveSourceId(already.id);
            setOpenMenu('source');
            setError(null);
            setSourceNotice(`«${already.label || file.name}» ya está en este reporte. La seleccionamos para que no se duplique.`);
            if (!(sheetsBySource[already.id] || []).length) {
                handleLoadWorksheets(already.input || url, null, already.id);
            }
            return;
        }
        const targetId = activeSource?.kind === 'join' ? null : activeSource?.id;
        const emptyTarget = targetId && !activeSource?.spreadsheet_id;
        const defaultLabel = !activeSource?.label || /^Fuente \d+$/.test(activeSource.label);
        setSourceNotice(null);
        if (targetId && (emptyTarget || defaultLabel)) {
            patchSource(targetId, {
                input: url,
                spreadsheet_id: file.id,
                label: defaultLabel ? file.name : activeSource.label,
            });
            handleLoadWorksheets(url, null, targetId);
            return;
        }
        const created = createDataSource({
            label: file.name || `Fuente ${sheetSources.length + 1}`,
            spreadsheet_id: file.id,
            input: url,
        });
        const catalog = [...dataSources, created];
        setDataSources(catalog);
        setActiveSourceId(created.id);
        setOpenMenu('source');
        handleLoadWorksheets(url, null, created.id, { catalog });
    };

    const openBlend = () => {
        const existing = dataSources.find(item => item.kind === 'join');
        if (existing) setActiveSourceId(existing.id);
        setJoinMapOpen(true);
    };

    const handlePatchJoin = (joinId, partial) => {
        const current = joins.find(item => item.id === joinId);
        if (!current) return;
        const nextJoin = { ...current, ...partial };
        const nextJoins = joins.map(item => item.id === joinId ? nextJoin : item);
        setJoins(nextJoins);
        const joinSource = dataSources.find(item => item.joinId === joinId);
        if (joinSource && (partial.leftDataSourceId || partial.rightDataSourceId)) {
            const left = dataSources.find(item => item.id === nextJoin.leftDataSourceId);
            const right = dataSources.find(item => item.id === nextJoin.rightDataSourceId);
            patchSource(joinSource.id, { label: `${sourceLabel(left)} + ${sourceLabel(right)}` });
        }
    };

    const handleBlendSources = (leftId, rightId) => {
        if (wouldCreateJoinCycle(dataSources, joins, leftId, rightId)) {
            setError('Esa unión formaría un ciclo o usa la misma fuente dos veces.');
            return;
        }
        const existing = joins.find(join =>
            (join.leftDataSourceId === leftId && join.rightDataSourceId === rightId)
            || (join.leftDataSourceId === rightId && join.rightDataSourceId === leftId)
        );
        if (existing) {
            const source = dataSources.find(item => item.joinId === existing.id);
            if (source) setActiveSourceId(source.id);
            setError(null);
            setSourceNotice('Esta unión ya existe. La seleccionamos para que la puedas editar.');
            return;
        }
        const left = dataSources.find(item => item.id === leftId);
        const right = dataSources.find(item => item.id === rightId);
        if (!left || !right) return;
        const keys = guessJoinKeys(
            schemasBySource[leftId]?.columns || [],
            schemasBySource[rightId]?.columns || []
        );
        const join = createJoin({
            leftDataSourceId: leftId,
            rightDataSourceId: rightId,
            keys,
        });
        const joinSource = createDataSource({
            kind: 'join',
            label: `${sourceLabel(left)} + ${sourceLabel(right)}`,
            joinId: join.id,
        });
        const nextJoins = [...joins, join];
        const nextSources = [...dataSources, joinSource];
        setJoins(nextJoins);
        setDataSources(nextSources);
        setActiveSourceId(joinSource.id);
        setError(null);
        setSourceNotice('Unión creada. Elige las columnas clave y pulsa Probar unión.');
        if (keys.some(key => key.left && key.right)) {
            handleDiscoverColumns({ sourceId: joinSource.id, dataSources: nextSources, joins: nextJoins });
        }
    };

    const openTheme = () => {
        setPropsOpen(true);
        setCanvasOpen(true);
        setOpenMenu(null);
    };

    const handleRemoveSource = (sourceId) => {
        const source = dataSources.find(item => item.id === sourceId);
        if (source?.kind === 'join') {
            handleRemoveJoin(source.joinId);
            return;
        }
        const used = widgets.some(widget => widget.query?.dataSourceId === sourceId)
            || joins.some(join => join.leftDataSourceId === sourceId || join.rightDataSourceId === sourceId);
        if (used && !window.confirm('Esta fuente está en uso. ¿Eliminarla igual? También se quitarán las uniones que la usan.')) return;
        applyCatalogPrune(
            pruneJoinGraph(dataSources, joins, [sourceId]),
            'Fuente eliminada.'
        );
    };

    const handleAddJoin = () => {
        if (sheetSources.length < 2) {
            setError('Conecta al menos dos hojas para crear una unión.');
            return;
        }
        const left = sheetSources[0];
        const right = sheetSources[1];
        const existing = joins.find(join =>
            (join.leftDataSourceId === left.id && join.rightDataSourceId === right.id)
            || (join.leftDataSourceId === right.id && join.rightDataSourceId === left.id)
        );
        handleBlendSources(left.id, right.id);
        if (!existing) {
            setSourceNotice('Unión con las dos primeras hojas. Arrastra otras en el lienzo para combinarlas.');
        }
    };

    const handleAddWidget = (type) => {
        const widgetSource = dataSources.find(item => item.id === (selectedWidget?.query?.dataSourceId || activeSource?.id)) || activeSource;
        const ready = widgetSource && (
            widgetSource.kind === 'join'
                ? schemasBySource[widgetSource.id]?.columns?.length
                : widgetSource.spreadsheet_id && widgetSource.sheet_name && schemasBySource[widgetSource.id]?.columns?.length
        );
        if (!isDecorationType(type) && !isContainerType(type) && !ready) {
            setError('Conecta primero una hoja de cálculo para añadir widgets.');
            setOpenMenu('source');
            return;
        }
        captureHistory();
        const widget = createWidget(type, {
            id: widgetSource?.id,
            spreadsheet_id: widgetSource?.spreadsheet_id || '',
            sheet_name: widgetSource?.sheet_name || ''
        });
        const host = findWidgetById(widgets, selectedId);
        const parent = findParentWidget(widgets, selectedId);
        const target = (host && isContainerType(host.type) ? host : parent);
        const siblings = target && isContainerType(target.type)
            ? (target.children?.widgets || [])
            : widgets;
        widget.z = Math.max(-1, ...siblings.map(item => widgetZ(item))) + 1;
        if (target && isContainerType(target.type)) {
            const nextHost = addWidgetToContainer(target, widget, INNER_CANVAS_COLUMNS);
            setWidgets(prev => updateWidgetById(prev, target.id, () => nextHost));
            setLayout(prev => prev.map(item => {
                if (item.i !== target.id) return item;
                return { ...item, h: Math.max(item.h || 6, containerHeightForInner(nextHost.children?.layout, canvas.rowHeight)) };
            }));
            setSelectedId(widget.id);
            setSelectedIds([widget.id]);
        } else {
            setWidgets(prev => [...prev, widget]);
            setLayout(prev => [...prev, createLayoutItem(widget, prev, canvas.columns)]);
            setSelectedId(widget.id);
            setSelectedIds([widget.id]);
        }
        setOpenMenu(null);
        if (type === 'slicer' && widget.filterId) {
            setFilterDefs(prev => [...prev, {
                id: widget.filterId,
                column: '',
                label: widget.title,
                type: 'multiselect',
                options: []
            }]);
        }
    };

    const clearFilterIds = (ids) => {
        if (!ids?.length) return;
        setFilterDefs(prev => prev.filter(item => !ids.includes(item.id)));
        setFilterValues(prev => {
            const next = { ...prev };
            ids.forEach(id => { delete next[id]; });
            return next;
        });
    };

    const handleDeleteWidget = (id, event) => {
        event.stopPropagation();
        captureHistory();
        const victim = findWidgetById(widgets, id);
        clearFilterIds(collectFilterIds(victim));
        setWidgets(prev => removeWidgetById(prev, id));
        setLayout(prev => prev.filter(item => item.i !== id));
        if (selectedId === id) setSelectedId(findParentWidget(widgets, id)?.id || null);
        setSelectedIds(prev => prev.filter(item => item !== id));
    };

    const handleWidgetChange = (nextWidget) => {
        captureHistory();
        const kpi = nextWidget.query?.kpi || {};
        if (normalizeWidgetType(nextWidget.type) === 'kpi' && (kpi.compare || kpi.sparkGrain || kpi.targetMode)) {
            setLayout(prev => prev.map(item => (
                item.i === nextWidget.id && item.h < KPI_EXTRAS_MIN_ROWS ? { ...item, h: KPI_EXTRAS_MIN_ROWS } : item
            )));
        }
        setWidgets(prev => updateWidgetById(prev, nextWidget.id, () => nextWidget));
        if (nextWidget.query?.dataSourceId) setActiveSourceId(nextWidget.query.dataSourceId);
    };

    const handleSelectWidget = (id, event) => {
        if (event?.ctrlKey || event?.metaKey) {
            setSelectedIds(prev => {
                const next = prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id];
                setSelectedId(next[next.length - 1] || null);
                return next;
            });
            return;
        }
        setSelectedId(id);
        setSelectedIds([id]);
        // Al elegir un elemento, sus propiedades se abren solas.
        setElementOpen(true);
        const picked = findWidgetById(widgets, id);
        if (picked?.query?.dataSourceId) setActiveSourceId(picked.query.dataSourceId);
    };

    const handleDuplicateWidget = (id) => {
        const source = findWidgetById(widgets, id);
        if (!source) return;
        captureHistory();
        const copy = cloneWidget(source);
        const parent = findParentWidget(widgets, id);
        const siblings = parent ? (parent.children?.widgets || []) : widgets;
        copy.z = Math.max(-1, ...siblings.map(item => widgetZ(item))) + 1;
        if (parent) {
            const nextHost = addWidgetToContainer(parent, copy, INNER_CANVAS_COLUMNS);
            setWidgets(prev => updateWidgetById(prev, parent.id, () => nextHost));
            setLayout(prev => prev.map(item => {
                if (item.i !== parent.id) return item;
                return { ...item, h: Math.max(item.h || 6, containerHeightForInner(nextHost.children?.layout, canvas.rowHeight)) };
            }));
        } else {
            setWidgets(prev => [...prev, copy]);
            setLayout(prev => [...prev, createLayoutItem(copy, prev, canvas.columns)]);
        }
        setSelectedId(copy.id);
        setSelectedIds([copy.id]);
        const slicers = [];
        const walk = (node) => {
            if (node.type === 'slicer' && node.filterId) {
                slicers.push({
                    id: node.filterId,
                    column: node.query?.dimensions?.[0] || '',
                    label: node.title,
                    type: 'multiselect',
                    options: []
                });
            }
            (node.children?.widgets || []).forEach(walk);
        };
        walk(copy);
        if (slicers.length) setFilterDefs(prev => [...prev, ...slicers]);
    };

    const handleGroupSelected = () => {
        if (selectedIds.length < 2) return;
        captureHistory();
        const groupId = `group-${Date.now()}`;
        setWidgets(prev => prev.map(w => selectedIds.includes(w.id) ? { ...w, groupId } : w));
    };

    const handleUngroupSelected = () => {
        const ids = selectedIds.length ? selectedIds : (selectedId ? [selectedId] : []);
        if (ids.length === 0) return;
        captureHistory();
        setWidgets(prev => prev.map(w => ids.includes(w.id) ? { ...w, groupId: undefined } : w));
    };

    const selectionArrangeContext = () => {
        const ids = selectedIds.length ? selectedIds : (selectedId ? [selectedId] : []);
        if (!ids.length) return null;
        const parent = findParentWidget(widgets, ids[0]);
        const sameParent = ids.every(id => (findParentWidget(widgets, id)?.id || null) === (parent?.id || null));
        if (!sameParent) return null;
        if (parent) {
            return {
                parent,
                list: parent.children?.widgets || [],
                layout: parent.children?.layout || [],
                ids,
                cols: INNER_CANVAS_COLUMNS,
                rows: Math.max(8, ...(parent.children?.layout || []).map(item => (item.y || 0) + (item.h || 1)))
            };
        }
        if (!ids.every(id => widgets.some(item => item.id === id))) return null;
        return {
            parent: null,
            list: widgets,
            layout,
            ids,
            cols: canvas.columns,
            rows: canvasGridRows(canvas)
        };
    };

    const handleLayer = (op) => {
        const ctx = selectionArrangeContext();
        if (!ctx) {
            setError('Selecciona elementos del mismo lienzo para ordenarlos o alinearlos.');
            return;
        }
        captureHistory();
        const nextList = layerWidgets(ctx.list, ctx.ids, op);
        if (ctx.parent) {
            setWidgets(prev => updateWidgetById(prev, ctx.parent.id, current => ({
                ...current,
                children: { ...current.children, widgets: nextList }
            })));
            return;
        }
        setWidgets(nextList);
    };

    const handleAlign = (mode) => {
        const ctx = selectionArrangeContext();
        if (!ctx) {
            setError('Selecciona elementos del mismo lienzo para ordenarlos o alinearlos.');
            return;
        }
        captureHistory();
        const nextLayout = alignLayoutItems(ctx.layout, ctx.ids, mode, { cols: ctx.cols, rows: ctx.rows });
        if (ctx.parent) {
            setWidgets(prev => updateWidgetById(prev, ctx.parent.id, current => ({
                ...current,
                children: { ...current.children, layout: nextLayout }
            })));
            return;
        }
        setLayout(nextLayout);
    };

    const handlePackIntoContainer = () => {
        const ids = selectedIds.filter(id => widgets.some(w => w.id === id));
        if (ids.length < 1) return;
        captureHistory();
        const packed = widgets.filter(w => ids.includes(w.id));
        const remaining = widgets.filter(w => !ids.includes(w.id));
        const packedLayout = layout.filter(item => ids.includes(item.i));
        const remainingLayout = layout.filter(item => !ids.includes(item.i));
        if (packedLayout.length === 0) return;
        const minX = Math.min(...packedLayout.map(item => item.x || 0));
        const minY = Math.min(...packedLayout.map(item => item.y || 0));
        const maxX = Math.max(...packedLayout.map(item => (item.x || 0) + (item.w || 1)));
        const maxY = Math.max(...packedLayout.map(item => (item.y || 0) + (item.h || 1)));
        const container = createWidget('container', {
            id: activeSource?.id,
            spreadsheet_id: spreadsheetId,
            sheet_name: sheetName
        });
        container.title = 'Bloque';
        const innerLayout = reflowLayout(packedLayout.map(item => ({
            ...item,
            x: Math.max(0, (item.x || 0) - minX),
            y: Math.max(0, (item.y || 0) - minY),
            w: Math.min(INNER_CANVAS_COLUMNS, item.w || 1),
            maxW: INNER_CANVAS_COLUMNS
        })), INNER_CANVAS_COLUMNS);
        container.children = {
            widgets: packed.map(w => ({
                ...w,
                groupId: undefined,
                style: { ...(w.style || {}), valign: w.style?.valign || 'top' }
            })),
            layout: innerLayout
        };
        const box = {
            i: container.id,
            x: minX,
            y: minY,
            w: Math.min(canvas.columns, Math.max(4, maxX - minX)),
            h: Math.max(6, containerHeightForInner(innerLayout, canvas.rowHeight), maxY - minY),
            minW: 3,
            minH: 5,
            maxW: canvas.columns
        };
        setWidgets([...remaining, container]);
        setLayout([...remainingLayout, box]);
        setSelectedId(container.id);
        setSelectedIds([container.id]);
    };

    const clientToCanvasSlot = (clientX, clientY, w, h) => {
        const cols = canvas.columns;
        const rowH = canvas.rowHeight || 48;
        const el = canvasRef.current?.querySelector('.react-grid-layout') || canvasRef.current;
        if (!el || clientX == null || clientY == null) {
            return findSlotNear(layoutRef.current, w, h, cols);
        }
        const rect = el.getBoundingClientRect();
        const margin = 10;
        const colWidth = Math.max(1, (rect.width - margin * (cols + 1)) / cols);
        const x = Math.max(0, Math.min(cols - w, Math.round((clientX - rect.left - margin) / (colWidth + margin))));
        const y = Math.max(0, Math.round((clientY - rect.top - margin) / (rowH + margin)));
        return findSlotNear(layoutRef.current, w, h, cols, x, y);
    };

    const handleExtractChild = (childId, clientX, clientY) => {
        const parent = findParentWidget(widgets, childId);
        const child = findWidgetById(widgets, childId);
        if (!parent || !child) return;
        captureHistory();
        extractLockRef.current = true;
        const innerItem = (parent.children?.layout || []).find(item => item.i === childId);
        const w = Math.max(2, Math.min(canvas.columns, Math.round(((innerItem?.w || 4) / INNER_CANVAS_COLUMNS) * canvas.columns)));
        const h = Math.max(2, innerItem?.h || 3);
        const slot = clientToCanvasSlot(clientX, clientY, w, h);
        setWidgets(prev => {
            const lifted = findWidgetById(prev, childId);
            if (!lifted || !findParentWidget(prev, childId)) return prev;
            return [...removeWidgetById(prev, childId), lifted];
        });
        setLayout(prev => {
            if (prev.some(item => item.i === childId)) return prev;
            return [...prev, {
                i: childId,
                x: slot.x,
                y: slot.y,
                w,
                h,
                minW: 1,
                minH: 1,
                maxW: canvas.columns
            }];
        });
        setSelectedId(childId);
        setSelectedIds([childId]);
        setExtractingFrom(null);
        window.setTimeout(() => { extractLockRef.current = false; }, 200);
    };

    const handleUnpackContainer = () => {
        const box = findWidgetById(widgets, selectedId);
        if (!isContainerType(box?.type)) return;
        captureHistory();
        const item = layout.find(entry => entry.i === box.id);
        const children = box.children?.widgets || [];
        const lifted = (box.children?.layout || []).map(entry => ({
            ...entry,
            x: (item?.x || 0) + (entry.x || 0),
            y: (item?.y || 0) + (entry.y || 0),
            maxW: canvas.columns
        }));
        setWidgets(prev => [...removeWidgetById(prev, box.id), ...children]);
        setLayout(prev => [...prev.filter(entry => entry.i !== box.id), ...lifted]);
        setSelectedId(children[0]?.id || null);
        setSelectedIds(children.map(child => child.id));
    };

    const applyGroupedLayout = (current) => {
        if (!Array.isArray(current) || current.length === 0) return;
        if (layoutSignature(current) === layoutSignature(layoutRef.current)) return;
        const prev = layoutRef.current;
        const moved = current.find(item => {
            const old = prev.find(p => p.i === item.i);
            return old && (old.x !== item.x || old.y !== item.y);
        });
        if (!moved) {
            layoutRef.current = current;
            setLayout(current);
            return;
        }
        const widget = widgets.find(w => w.id === moved.i);
        const old = prev.find(p => p.i === moved.i);
        if (!widget?.groupId || !old) {
            layoutRef.current = current;
            setLayout(current);
            return;
        }
        const dx = moved.x - old.x;
        const dy = moved.y - old.y;
        if (!dx && !dy) {
            layoutRef.current = current;
            setLayout(current);
            return;
        }
        const groupIds = new Set(widgets.filter(w => w.groupId === widget.groupId).map(w => w.id));
        const next = current.map(item => {
            if (item.i === moved.i || !groupIds.has(item.i)) return item;
            const prevItem = prev.find(p => p.i === item.i);
            if (!prevItem) return item;
            return { ...item, x: Math.max(0, prevItem.x + dx), y: Math.max(0, prevItem.y + dy) };
        });
        layoutRef.current = next;
        setLayout(next);
    };

    useEffect(() => {
        layoutRef.current = layout;
    }, [layout]);

    useEffect(() => {
        const onKey = (event) => {
            // Ctrl+S guarda aunque el foco esté en un campo, igual que en cualquier editor.
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
                event.preventDefault();
                saveKeepingState();
                return;
            }
            const tag = event.target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (event.key === '?' && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                setShortcutsOpen(v => !v);
                return;
            }
            if ((event.ctrlKey || event.metaKey) && (event.key.toLowerCase() === 'y' || (event.shiftKey && event.key.toLowerCase() === 'z'))) {
                event.preventDefault();
                handleRedo();
                return;
            }
            if (event.key === 'Escape') {
                if (shortcutsOpen) {
                    event.preventDefault();
                    setShortcutsOpen(false);
                    return;
                }
                if (previewOpen) {
                    event.preventDefault();
                    setPreviewOpen(false);
                    return;
                }
                if (openMenu) {
                    event.preventDefault();
                    setOpenMenu(null);
                    return;
                }
            }
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
                event.preventDefault();
                handleUndo();
                return;
            }
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd' && selectedId) {
                event.preventDefault();
                handleDuplicateWidget(selectedId);
            }
            if (event.key === 'Delete' && selectedId) {
                event.preventDefault();
                handleDeleteWidget(selectedId, { stopPropagation() {} });
            }
            if ((event.ctrlKey || event.metaKey) && event.code === 'BracketRight' && selectedId) {
                event.preventDefault();
                handleLayer(event.shiftKey ? 'front' : 'forward');
            }
            if ((event.ctrlKey || event.metaKey) && event.code === 'BracketLeft' && selectedId) {
                event.preventDefault();
                handleLayer(event.shiftKey ? 'back' : 'backward');
            }
            if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
                && !event.ctrlKey && !event.metaKey && !event.altKey
                && (selectedIds.length || selectedId)) {
                event.preventDefault();
                const step = event.shiftKey ? 4 : 1;
                const delta = {
                    ArrowLeft: [-step, 0],
                    ArrowRight: [step, 0],
                    ArrowUp: [0, -step],
                    ArrowDown: [0, step]
                }[event.key];
                nudgeSelection(delta[0], delta[1]);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    /**
     * Precalcula los datos de cada hoja publicada y los guarda en Storage.
     * Los payloads son los mismos que arma el visor con los filtros sin tocar.
     */
    const publishSnapshots = async (reportId, pagesToSave) => {
        const catalog = { dataSources, joins };
        const collect = (widgets, pageFilters, out) => {
            (widgets || []).forEach((widget) => {
                if (widget.children?.widgets?.length) collect(widget.children.widgets, pageFilters, out);
                const type = normalizeWidgetType(widget.type);
                if (type === 'slicer' || isDecorationType(type) || isContainerType(type)) return;
                const query = widget.query || {};
                if (!widgetHasQueryShape(query)) return;
                out.push({
                    id: widget.id,
                    payload: buildWidgetQueryPayload(
                        query,
                        catalog,
                        buildEngineFilters(pageFilters, {}, query.dataSourceId, catalog),
                        widget
                    )
                });
            });
            return out;
        };

        const pages = (pagesToSave || [])
            .map(page => ({ pageId: page.id, queries: collect(page.widgets, page.filters || [], []) }))
            .filter(page => page.queries.length);
        if (!pages.length) return;

        try {
            await fetchWithAuth(`${API_URL}/api/reports/${reportId}/snapshot`, {
                method: 'POST',
                silent: true,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pages })
            });
        } catch (err) {
            setSourceNotice(`El reporte se publicó, pero los datos precargados no se guardaron: ${err.message}`);
        }
    };

    const handleSaveReport = async (asDraft = false, options = {}) => {
        const silent = !!options.silent;
        if (saveLockRef.current) return false;
        if (readOnly) {
            if (!silent) alert('Tu acceso a este reporte es solo de vista: no puedes guardar cambios. Duplícalo desde el inicio para trabajar en una copia.');
            return false;
        }
        if (!selectedClientId || !selectedProjectId || !reportNameInput.trim()) {
            if (!silent) {
                alert(asDraft
                    ? 'Selecciona cliente, proyecto y nombre del reporte antes de guardar el borrador.'
                    : 'Selecciona cliente, proyecto y nombre del reporte antes de publicar.');
            }
            return false;
        }
        const pagesToSave = flushedPages.map(page => ({
            id: page.id,
            name: page.name,
            canvas: page.canvas || { ...DEFAULT_CANVAS },
            layout: (page.layout || []).map(({ i, x, y, w, h, minW, minH }) => ({ i, x, y, w, h, minW, minH })),
            widgets: page.widgets || [],
            filters: (page.filters || []).map(def => ({
                ...def,
                options: (schemasBySource[def.dataSourceId]?.columns || schemaColumns).find(col => col.name === def.column)?.values || []
            })),
            ...pageNavPayload(page)
        }));
        if (!pagesToSave.some(page => (page.widgets || []).length > 0)) {
            if (!silent) alert('Añade al menos un widget en alguna hoja.');
            return false;
        }
        const snap = getSnapshotRef.current();
        if (silent && snap && snap === savedSnapshotRef.current) return false;

        saveLockRef.current = true;
        if (silent) setAutoSaving(true);
        else setSavingReport(true);
        setError(null);

        const selectedClient = clients.find(c => c.id === selectedClientId);
        const selectedProject = projects.find(p => p.id === selectedProjectId);
        const activeSaved = pagesToSave.find(page => page.id === activePageId) || pagesToSave[0];

        const reportPayload = {
            cliente: selectedClient ? selectedClient.nombre : '',
            clientId: selectedClientId,
            proyecto: selectedProject ? selectedProject.nombre : '',
            projectId: selectedProjectId,
            reporteNombre: reportNameInput.trim(),
            isCustom: true,
            isDraft: !!asDraft,
            dashboardUrl: editingReportId ? `custom:${editingReportId}` : 'custom-report-view',
            customConfig: JSON.stringify({
                version: 5,
                dataSource: {
                    spreadsheet_id: sheetSources[0]?.spreadsheet_id || spreadsheetId,
                    sheet_name: sheetSources[0]?.sheet_name || sheetName
                },
                dataSources,
                joins,
                calculatedFields,
                canvas: activeSaved.canvas,
                // Nivel-raíz solo por compatibilidad hacia atrás; la fuente real es pages[].filters.
                filters: activeSaved.filters || [],
                // Columnas por fuente: el visor ya no necesita pedir el esquema al abrir.
                schemas: Object.fromEntries(Object.entries(schemasBySource).map(([sourceId, info]) => [
                    sourceId,
                    {
                        columns: (info?.columns || []).map(col => ({
                            name: col.name,
                            inferred_type: col.inferred_type,
                            unique_count: col.unique_count
                        })),
                        rowCount: info?.rowCount ?? null
                    }
                ])),
                pages: pagesToSave,
                sections,
                activePageId,
                theme: reportTheme,
                layout: activeSaved.layout,
                widgets: activeSaved.widgets
            })
        };

        try {
            const persist = async () => {
                let reportId = editingReportId;
                if (editingReportId) {
                    await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${editingReportId}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(reportPayload)
                    });
                } else {
                    const res = await fetchWithAuth(`${API_URL}/admin/reportes-maestros`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(reportPayload)
                    });
                    const data = await res.json().catch(() => ({}));
                    if (data.id) {
                        reportId = data.id;
                        setEditingReportId(data.id);
                    }
                }
                // Al publicar se precalculan los datos de cada hoja para que el visor
                // abra sin esperar al motor. Si falla, el reporte sigue publicado.
                if (!asDraft && reportId) {
                    await publishSnapshots(reportId, pagesToSave);
                }
                if (refreshAllData) await refreshAllData();
                isDraftRef.current = !!asDraft;
                if (onSaved) onSaved();
                savedSnapshotRef.current = getSnapshotRef.current();
                setSavedVersion(v => v + 1);
                setLastSavedAt(new Date());
            };
            if (silent) {
                await persist();
            } else {
                await withBusy(persist, asDraft ? 'Guardando borrador…' : 'Publicando dashboard…');
                alert(asDraft
                    ? 'Borrador guardado. Aún no aparece para los usuarios finales.'
                    : 'Dashboard personalizado publicado en la Biblioteca.');
            }
            return true;
        } catch (err) {
            if (!silent) setError(`Error al guardar: ${err.message}`);
            else setSourceNotice(`Autoguardado: ${err.message}`);
            return false;
        } finally {
            saveLockRef.current = false;
            setSavingReport(false);
            setAutoSaving(false);
        }
    };
    saveReportRef.current = handleSaveReport;

    /** Guarda como lo haría el autoguardado: un reporte publicado sigue publicado. */
    const saveKeepingState = () => {
        const { editingReportId: reportId } = autoSaveCtxRef.current;
        const asDraft = !reportId || isDraftRef.current !== false;
        saveReportRef.current(asDraft);
    };

    // Hay cambios sin guardar si difiere de la última línea base; un reporte nuevo, si ya tiene elementos.
    const hasContent = flushedPages.some(page => (page.widgets || []).length > 0);
    // La foto del reporte solo se recalcula cuando cambia su contenido, no en cada render.
    const currentSnapshot = useMemo(
        () => getSnapshotRef.current(),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mismas entradas que getSnapshotRef
        [dataSources, joins, calculatedFields, flushedPages, reportTheme, sections, reportNameInput, selectedClientId, selectedProjectId]
    );
    const dirty = loadingBaselineRef.current
        ? false
        : (savedSnapshotRef.current
            ? currentSnapshot !== savedSnapshotRef.current
            : hasContent);
    dirtyRef.current = dirty;

    useEffect(() => {
        setCreatorDirtyCheck(() => dirtyRef.current);
        return () => setCreatorDirtyCheck(null);
    }, []);

    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    useEffect(() => {
        if (!autoSaveEnabled) return undefined;
        const ms = Math.max(1, autoSaveMinutes) * 60 * 1000;
        const id = window.setInterval(() => {
            const { editingReportId: reportId } = autoSaveCtxRef.current;
            const asDraft = !reportId || isDraftRef.current !== false;
            saveReportRef.current(asDraft, { silent: true });
        }, ms);
        return () => window.clearInterval(id);
    }, [autoSaveEnabled, autoSaveMinutes]);

    const persistAutoSaveEnabled = (enabled) => {
        setAutoSaveEnabled(enabled);
        try { window.localStorage.setItem(AUTOSAVE_ENABLED_KEY, enabled ? '1' : '0'); } catch (err) { /* ignore */ }
    };
    const persistAutoSaveMinutes = (minutes) => {
        const next = AUTOSAVE_CHOICES.includes(minutes) ? minutes : 5;
        setAutoSaveMinutes(next);
        try { window.localStorage.setItem(AUTOSAVE_MINUTES_KEY, String(next)); } catch (err) { /* ignore */ }
    };

    /** Archivo → Nuevo / Abrir: el contenedor monta un editor nuevo (pide confirmar si hay cambios). */
    const loadSavedReport = (reportId) => {
        if (!reportId) {
            if (onNewReport) onNewReport();
            return;
        }
        if (onOpenReport) onOpenReport(reportId);
    };

    /** Carga en el editor la configuración guardada de un reporte. */
    const applyReport = (report) => {
        if (!report?.customConfig) {
            setError('Ese dashboard no tiene una configuración válida.');
            return;
        }
        try {
            const raw = typeof report.customConfig === 'string'
                ? JSON.parse(report.customConfig)
                : report.customConfig;
            const { pages: loadedPages, activePageId: loadedPageId, sections: loadedSections } = normalizeReportPages(raw);
            const activeLoaded = loadedPages.find(page => page.id === loadedPageId) || loadedPages[0];
            const { dataSources: loadedSources, joins: loadedJoins } = normalizeDataSources(raw);
            const legacyId = loadedSources[0]?.id;
            setReportTheme(normalizeReportTheme(raw.theme));
            setDataSources(loadedSources);
            setJoins(loadedJoins);
            setActiveSourceId(legacyId || '');
            setCalculatedFields(raw.calculatedFields || []);
            setPages(loadedPages.map(page => ({
                ...page,
                widgets: applySourceIdToWidgets(page.widgets || [], legacyId)
            })));
            setSections(loadedSections || []);
            setActivePageId(activeLoaded.id);
            setCanvas({ ...DEFAULT_CANVAS, ...(activeLoaded.canvas || {}) });
            setFilterDefs(activeLoaded.filters || []);
            setFilterValues({});
            setWidgets(applySourceIdToWidgets(activeLoaded.widgets || [], legacyId));
            setLayout(activeLoaded.layout || []);
            historyRef.current = [];
            setCanUndo(false);
            redoRef.current = [];
            setCanRedo(false);
            setSelectedClientId(report.clientId || '');
            setSelectedProjectId(report.projectId || '');
            setReportNameInput(report.reporteNombre || '');
            setEditingReportId(report.id);
            setSelectedId(null);
            setError(null);
            savedSnapshotRef.current = '';
            setLastSavedAt(null);
            loadingBaselineRef.current = true;
            (async () => {
                // Todas las hojas a la vez y desde la caché del motor; luego las uniones.
                await loadSourceCatalog(loadedSources, loadedJoins, false);
                // Línea base de «sin cambios» una vez que la carga terminó de pintar.
                await new Promise(resolve => { window.setTimeout(resolve, 60); });
                savedSnapshotRef.current = getSnapshotRef.current();
                loadingBaselineRef.current = false;
                setSavedVersion(v => v + 1);
            })();
        } catch (err) {
            setError('No se pudo cargar el dashboard guardado.');
        }
    };

    // Al montar: el reporte que eligió el inicio, o uno nuevo con el cliente/proyecto sugerido.
    useEffect(() => {
        if (initialReport) {
            applyReport(initialReport);
        } else if (initialPreset?.clientId) {
            setSelectedClientId(initialPreset.clientId);
            if (initialPreset.projectId) setSelectedProjectId(initialPreset.projectId);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const patchCanvasColumns = (raw) => {
        const columns = Math.max(24, Math.min(96, Number(raw) || CANVAS_GRID_COLUMNS));
        setCanvas(prev => {
            const from = prev.columns || CANVAS_GRID_COLUMNS;
            if (from !== columns) {
                setLayout(current => scaleLayout(current, from, columns, 1));
            }
            return { ...prev, columns };
        });
    };

    const nudgeSelection = (dx, dy) => {
        const ctx = selectionArrangeContext();
        if (!ctx || (!dx && !dy)) return;
        captureHistory();
        const cols = ctx.cols;
        const ids = new Set(ctx.ids);
        const next = (ctx.layout || []).map(item => {
            if (!ids.has(item.i)) return item;
            const w = Math.max(1, item.w || 1);
            return {
                ...item,
                x: Math.max(0, Math.min(Math.max(0, cols - w), (item.x || 0) + dx)),
                y: Math.max(0, (item.y || 0) + dy)
            };
        });
        if (ctx.parent) {
            setWidgets(prev => updateWidgetById(prev, ctx.parent.id, current => ({
                ...current,
                children: { ...(current.children || {}), layout: next }
            })));
        } else {
            layoutRef.current = next;
            setLayout(next);
        }
    };

    return (
        <DashboardThemeContext.Provider value={reportTheme}>
        <div style={localStyles.workspace}>
            <CreatorStudioBar
                openMenu={openMenu}
                onToggleMenu={toggleMenu}
                onCloseMenus={() => setOpenMenu(null)}
                reportName={reportNameInput}
                onReportNameChange={setReportNameInput}
                clients={clients}
                projects={projects}
                selectedClientId={selectedClientId}
                selectedProjectId={selectedProjectId}
                onClientChange={id => { setSelectedClientId(id); setSelectedProjectId(''); }}
                onProjectChange={setSelectedProjectId}
                customReports={customReports}
                editingReportId={editingReportId}
                onLoadReport={loadSavedReport}
                onGoHome={onGoHome}
                readOnly={readOnly}
                pages={flushedPages}
                activePageId={activePageId}
                onSelectPage={handleSelectPage}
                onAddPage={handleAddPage}
                onDeletePage={handleDeletePage}
                onRenamePage={handleRenamePage}
                onDuplicatePage={handleDuplicatePage}
                onAddSection={handleAddSection}
                pageOutlineOpen={pageOutlineOpen}
                onTogglePageOutline={() => setPageOutlineOpen(v => !v)}
                onOpenPageOutline={() => setPageOutlineOpen(true)}
                dataSourceCount={dataSources.length}
                canUndo={canUndo}
                onUndo={handleUndo}
                canRedo={canRedo}
                onRedo={handleRedo}
                dirty={dirty}
                onShowShortcuts={() => setShortcutsOpen(true)}
                onPreview={() => setPreviewOpen(true)}
                onSaveDraft={() => handleSaveReport(true)}
                onPublish={() => handleSaveReport(false)}
                savingReport={savingReport}
                autoSaving={autoSaving}
                autoSaveEnabled={autoSaveEnabled}
                autoSaveMinutes={autoSaveMinutes}
                autoSaveChoices={AUTOSAVE_CHOICES}
                lastSavedAt={lastSavedAt}
                onAutoSaveEnabledChange={persistAutoSaveEnabled}
                onAutoSaveMinutesChange={persistAutoSaveMinutes}
                canPreview={flushedPages.some(page => (page.widgets || []).length > 0)}
                onAddData={openAddData}
                onOpenDrive={openDrivePicker}
                onBlend={openBlend}
                onRefreshSources={handleRefreshAllSources}
                refreshingSources={refreshingSources}
                onAddWidget={handleAddWidget}
                onTheme={openTheme}
                onResetFilters={() => setFilterValues(prev => {
                    const ids = new Set(filterDefs.map(def => def.id));
                    return Object.fromEntries(Object.entries(prev).filter(([id]) => !ids.has(id)));
                })}
                paletteOpen={paletteOpen}
                propsOpen={propsOpen}
                onTogglePalette={() => setPaletteOpen(v => !v)}
                onToggleProps={() => setPropsOpen(v => !v)}
                selectedId={selectedId}
                selectedCount={selectedIds.filter(id => widgets.some(w => w.id === id)).length}
                selectedIsContainer={isContainerType(selectedWidget?.type)}
                hasGroup={selectedIds.some(id => widgets.find(w => w.id === id)?.groupId)}
                onDuplicateWidget={handleDuplicateWidget}
                onPack={handlePackIntoContainer}
                onUnpack={handleUnpackContainer}
                onGroup={handleGroupSelected}
                onUngroup={handleUngroupSelected}
                canArrange={selectedIds.length > 0}
                onLayer={handleLayer}
                onAlign={handleAlign}
            />

            {openMenu === 'source' && (
                <DataSourcesStudio
                    dataSources={dataSources}
                    joins={joins}
                    schemasBySource={schemasBySource}
                    activeSourceId={activeSourceId}
                    activeSource={activeSource}
                    onSelectSource={setActiveSourceId}
                    onAddSource={handleAddSource}
                    onAddJoin={handleAddJoin}
                    onRemoveSource={handleRemoveSource}
                    onRemoveJoin={handleRemoveJoin}
                    onClearJoins={handleClearJoins}
                    onPatchSource={patchSource}
                    onClose={() => setOpenMenu(null)}
                    spreadsheetInput={spreadsheetInput}
                    spreadsheetId={spreadsheetId}
                    sheetName={sheetName}
                    availableSheets={availableSheets}
                    onSheetChange={handleSheetChange}
                    onLoadWorksheets={handleLoadWorksheets}
                    sourceInUse={sourceInUse(activeSource?.id)}
                    onReplaceSource={(sourceId, initialInput, initialSheet) => openReplaceDialog({ mode: 'source', sourceId, initialInput, initialSheet })}
                    loadingSheets={loadingSheets}
                    loadingColumns={loadingColumns}
                    rowCount={rowCount}
                    schemaColumns={schemaColumns}
                    onOpenDrive={openDrivePicker}
                    onSourceFiltersChange={handleSourceFiltersChange}
                    onSourceColumnsChange={handleSourceColumnsChange}
                    onBlend={handleBlendSources}
                    onPatchJoin={handlePatchJoin}
                    onTestJoin={(sourceId) => handleDiscoverColumns({ sourceId })}
                    onOpenJoinMap={() => setJoinMapOpen(true)}
                />
            )}

            <JoinMapModal
                open={joinMapOpen}
                onClose={() => setJoinMapOpen(false)}
                dataSources={dataSources}
                joins={joins}
                schemasBySource={schemasBySource}
                activeSourceId={activeSourceId}
                loadingColumns={loadingColumns}
                onSelectSource={setActiveSourceId}
                onBlend={handleBlendSources}
                onPatchJoin={handlePatchJoin}
                onTestJoin={(sourceId) => handleDiscoverColumns({ sourceId })}
                onAddJoin={handleAddJoin}
                onRemoveJoin={handleRemoveJoin}
                onClearJoins={handleClearJoins}
            />

            {readOnly && (
                <div style={localStyles.notice}>
                    Solo lectura: tu acceso a este reporte es de vista. Puedes explorarlo, pero los cambios no se guardan.
                </div>
            )}
            {error && <div style={localStyles.error}>{error}</div>}
            {sourceNotice && !error && <div style={localStyles.notice}>{sourceNotice}</div>}
            <MissingFieldsBanner items={missingItems} onGoTo={goToMissing} />

            {replaceDialogProps && (
                <ReplaceSourceDialog
                    open
                    {...replaceDialogProps}
                    initialInput={replaceRequest?.initialInput || ''}
                    initialSheet={replaceRequest?.initialSheet || ''}
                    onClose={closeReplaceDialog}
                    onApply={handleApplyReplace}
                />
            )}

            <DriveSpreadsheetPicker
                open={drivePickerOpen}
                onClose={() => setDrivePickerOpen(false)}
                onSelect={handlePickDriveFile}
                usedIds={sheetSources.map(item => item.spreadsheet_id).filter(Boolean)}
            />

            <DashboardFilterBar
                filterDefs={filterDefs}
                filterValues={filterValues}
                schemaColumns={schemaColumns}
                schemasBySource={schemasBySource}
                dataSources={dataSources}
                joins={joins}
                dataSource={sourceRequestFields(activeSource || {}, { dataSources, joins })}
                onValuesChange={setFilterValues}
                editable={Object.values(schemasBySource).some(item => item.columns?.length)}
                onAddFilter={def => setFilterDefs(prev => [...prev, def])}
                onRemoveFilter={id => {
                    setFilterDefs(prev => prev.filter(item => item.id !== id));
                    setFilterValues(prev => {
                        const next = { ...prev };
                        delete next[id];
                        return next;
                    });
                }}
            />

            <div style={localStyles.body}>
                {pageOutlineOpen ? (
                    <ReportPageOutline
                        open
                        onClose={() => setPageOutlineOpen(false)}
                        pages={flushedPages}
                        sections={sections}
                        activePageId={activePageId}
                        onSelectPage={handleSelectPage}
                        onAddPage={handleAddPage}
                        onAddSection={handleAddSection}
                        onRenamePage={handleRenamePage}
                        onRenameSection={handleRenameSection}
                        onPatchPage={handlePatchPage}
                        onPatchSection={handlePatchSection}
                        onMovePage={handleMovePage}
                        onMoveSection={handleMoveSection}
                        onDeletePage={handleDeletePage}
                        onDeleteSection={handleDeleteSection}
                        onDropItem={handleOutlineDrop}
                    />
                ) : (
                    <button type="button" style={localStyles.rail} onClick={() => setPageOutlineOpen(true)} title="Mostrar índice de hojas">
                        Hojas
                    </button>
                )}
                {paletteOpen ? (
                <FieldPalette
                    schemaColumns={schemaColumns}
                    dataSourceId={paletteSourceId}
                    dataSources={dataSources}
                    joins={joins}
                    schemasBySource={schemasBySource}
                    onSelectSource={setActiveSourceId}
                    calculatedFields={calculatedFields}
                    draggingField={draggingField}
                    onDragField={setDraggingField}
                    onHide={() => setPaletteOpen(false)}
                    onClickField={(field) => {
                        if (selectedWidget && !isDecorationType(selectedWidget.type) && !isContainerType(selectedWidget.type)) {
                            assignField(selectedWidget, field, 'auto');
                        }
                    }}
                    onAddCalculated={field => setCalculatedFields(prev => [...prev, field])}
                    onUpdateCalculated={(prev, next) => {
                        if (prev) captureHistory();
                        const nextList = calculatedFields.map(item => (item.id === next.id ? next : item));
                        setCalculatedFields(nextList);
                        if (prev) setWidgets(widgetsNow => updateCalculatedInWidgets(widgetsNow, prev, next, nextList));
                    }}
                    onRemoveCalculated={field => {
                        captureHistory();
                        setCalculatedFields(prev => prev.filter(item => item.id !== field.id));
                        setWidgets(prev => removeCalculatedFromWidgets(prev, field));
                    }}
                />
                ) : (
                    <button type="button" style={localStyles.rail} onClick={() => setPaletteOpen(true)} title="Mostrar campos">
                        Campos
                    </button>
                )}

                <div style={localStyles.stageWell} className="creator-stage">
                <div
                    ref={canvasRef}
                    className="creator-artboard"
                    style={{
                    ...localStyles.artboard,
                    ...artboardSurface(canvas),
                    width: canvas.widthMode === 'px' ? canvas.width : '100%',
                    minHeight: canvas.height,
                    margin: canvas.widthMode === 'px' ? '0 auto' : undefined
                }}>
                    {widgets.length === 0 ? (
                        <div style={localStyles.placeholder}>
                            Esta hoja está vacía. Usa <strong>Añadir un gráfico</strong> o <strong>Añadir un control</strong> en la barra superior, o crea otra hoja.
                        </div>
                    ) : (
                        <>
                        <div style={localStyles.canvasToolbar}>
                            <span style={localStyles.canvasHint}>Lienzo libre · mueve y estira en las 4 direcciones · flechas 1 punto (Shift ×4) · los elementos pueden superponerse</span>
                            {selectedIds.length > 0 && (
                                <div style={localStyles.arrangeRow}>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleLayer('front')} title="Traer al frente">Al frente</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleLayer('forward')} title="Adelante">Adelante</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleLayer('backward')} title="Atrás">Atrás</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleLayer('back')} title="Enviar al fondo">Al fondo</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleAlign('top')} title="Alinear arriba">Arriba</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleAlign('middle')} title="Alinear centro vertical">Centro V</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleAlign('bottom')} title="Alinear abajo">Abajo</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleAlign('left')} title="Alinear a la izquierda">Izquierda</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleAlign('center')} title="Alinear centro horizontal">Centro H</button>
                                    <button type="button" style={localStyles.canvasBtn} onClick={() => handleAlign('right')} title="Alinear a la derecha">Derecha</button>
                                </div>
                            )}
                        </div>
                        <ResponsiveGridLayout
                            className="layout"
                            layouts={{ lg: layout }}
                            breakpoints={RGL_BREAKPOINTS}
                            cols={rglCols(canvas.columns)}
                            rowHeight={canvas.rowHeight}
                            margin={[4, 4]}
                            containerPadding={[8, 8]}
                            draggableHandle=".widget-drag-handle"
                            draggableCancel=".nested-text-editor,textarea,input,button"
                            compactType={null}
                            allowOverlap
                            preventCollision={false}
                            isBounded={false}
                            resizeHandles={CANVAS_RESIZE_HANDLES}
                            onLayoutChange={(current) => {
                                if (extractLockRef.current) return;
                                const ids = new Set(widgets.map(w => w.id));
                                const filtered = (current || []).filter(item => ids.has(item.i));
                                if (filtered.length > 0) applyGroupedLayout(filtered);
                            }}
                        >
                            {widgets.map(widget => (
                                <div
                                    key={widget.id}
                                    style={{
                                        ...localStyles.widgetCard,
                                        ...(isContainerType(widget.type) ? localStyles.blockCard : {}),
                                        backgroundColor: isContainerType(widget.type)
                                            ? (widget.style?.cardBackground || '#f4eefc')
                                            : (widget.style?.cardBackground || '#fff'),
                                        borderRadius: widget.style?.cardRadius ?? 10,
                                        ...(isContainerType(widget.type) ? getBlockFrameStyle(getWidgetStyle(widget)) : {}),
                                        overflow: extractingFrom === widget.id ? 'visible' : 'hidden',
                                        zIndex: extractingFrom === widget.id ? 1000 : (widgetZ(widget) + 1),
                                        outline: selectedIds.includes(widget.id)
                                            ? '2px solid #5E35B1'
                                            : (isContainerType(widget.type)
                                                ? '2px dashed #7e57c2'
                                                : (widget.groupId ? '2px dashed #00897B' : '1px solid #dadce0'))
                                    }}
                                    onClick={(e) => handleSelectWidget(widget.id, e)}
                                >
                                    <div
                                        className="widget-drag-handle"
                                        style={{
                                            ...localStyles.widgetChrome,
                                            ...(widget.style?.showHeader === false ? localStyles.widgetChromeCompact : {}),
                                            ...(isContainerType(widget.type) ? {
                                                background: 'transparent',
                                                borderBottom: 'none',
                                                alignItems: 'flex-start',
                                                padding: '0 0 4px',
                                                minHeight: 0
                                            } : {})
                                        }}
                                    >
                                        {isContainerType(widget.type) ? (
                                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {widget.style?.showTitle !== false && (
                                                    <input
                                                        className="nested-text-editor"
                                                        value={widget.title || ''}
                                                        placeholder="Título del bloque"
                                                        onChange={e => setWidgets(prev => updateWidgetById(prev, widget.id, current => ({
                                                            ...current,
                                                            title: e.target.value
                                                        })))}
                                                        onClick={e => e.stopPropagation()}
                                                        onMouseDown={e => e.stopPropagation()}
                                                        onKeyDown={e => e.stopPropagation()}
                                                        style={{
                                                            ...getBlockTitleStyle(getWidgetStyle(widget)),
                                                            border: 'none',
                                                            background: 'transparent',
                                                            width: '100%',
                                                            padding: 0,
                                                            outline: 'none'
                                                        }}
                                                    />
                                                )}
                                                {!!getWidgetStyle(widget).subtitle && (
                                                    <input
                                                        className="nested-text-editor"
                                                        value={getWidgetStyle(widget).subtitle || ''}
                                                        placeholder="Subtítulo"
                                                        onChange={e => setWidgets(prev => updateWidgetById(prev, widget.id, current => ({
                                                            ...current,
                                                            style: { ...current.style, subtitle: e.target.value }
                                                        })))}
                                                        onClick={e => e.stopPropagation()}
                                                        onMouseDown={e => e.stopPropagation()}
                                                        onKeyDown={e => e.stopPropagation()}
                                                        style={{
                                                            ...getBlockSubtitleStyle(getWidgetStyle(widget)),
                                                            border: 'none',
                                                            background: 'transparent',
                                                            width: '100%',
                                                            padding: 0,
                                                            outline: 'none'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        ) : widget.style?.showTitle !== false ? (
                                            <span style={localStyles.widgetTitle}>{widget.title || 'Sin título'}</span>
                                        ) : (
                                            <span style={{ flex: 1 }} />
                                        )}
                                        {missingById[widget.id] && (
                                            <span style={localStyles.missingBadge} title={`Sin equivalente en la fuente: ${missingById[widget.id].join(', ')}`}>
                                                Campo faltante
                                            </span>
                                        )}
                                        <span style={localStyles.widgetType}>
                                            {isContainerType(widget.type)
                                                ? 'bloque'
                                                : (widget.groupId ? `grupo · ${widget.type}` : widget.type)}
                                        </span>
                                        <button
                                            type="button"
                                            style={localStyles.dupBtn}
                                            title="Duplicar"
                                            onClick={(e) => { e.stopPropagation(); handleDuplicateWidget(widget.id); }}
                                        >⧉</button>
                                        <button
                                            type="button"
                                            style={localStyles.deleteBtn}
                                            onClick={(e) => handleDeleteWidget(widget.id, e)}
                                        >×</button>
                                    </div>
                                    <div style={{
                                        ...localStyles.widgetBody,
                                        ...(isContainerType(widget.type) ? localStyles.blockBody : {})
                                    }}>
                                        {draggingField && !isDecorationType(widget.type) && !isContainerType(widget.type) && (
                                            <div style={localStyles.dropOverlay}>
                                                {widget.type === 'slicer' ? (
                                                    <div
                                                        style={localStyles.dropHalfMet}
                                                        onDragOver={preventDragOver}
                                                        onDrop={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            assignField(widget, draggingField, 'dimension');
                                                            setDraggingField(null);
                                                        }}
                                                    >
                                                        Campo del filtro
                                                    </div>
                                                ) : (
                                                    <>
                                                {widget.type !== 'kpi' && (
                                                    <div
                                                        style={localStyles.dropHalfDim}
                                                        onDragOver={preventDragOver}
                                                        onDrop={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            assignField(widget, draggingField, 'dimension');
                                                            setDraggingField(null);
                                                        }}
                                                    >
                                                        Dimensión
                                                    </div>
                                                )}
                                                <div
                                                    style={localStyles.dropHalfMet}
                                                    onDragOver={preventDragOver}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        assignField(widget, draggingField, 'metric');
                                                        setDraggingField(null);
                                                    }}
                                                >
                                                    Métrica
                                                </div>
                                                <div
                                                    style={localStyles.dropHalfFilter}
                                                    onDragOver={preventDragOver}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        assignField(widget, draggingField, 'filter');
                                                        setDraggingField(null);
                                                    }}
                                                >
                                                    Filtro fijo
                                                </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        {isContainerType(widget.type) ? (
                                            <NestedWidgetCanvas
                                                container={widget}
                                                rowHeight={canvas.rowHeight}
                                                selectedIds={selectedIds}
                                                draggingField={draggingField}
                                                engineFilters={engineFilters}
                                                filterValues={filterValues}
                                                schemaColumns={schemaColumns}
                                                schemasBySource={schemasBySource}
                                                filterDefs={filterDefs}
                                                dataSources={dataSources}
                                                joins={joins}
                                                onSelect={handleSelectWidget}
                                                onDelete={handleDeleteWidget}
                                                onDuplicate={handleDuplicateWidget}
                                                onAssignField={(child, field, slot) => {
                                                    assignField(child, field, slot);
                                                    setDraggingField(null);
                                                }}
                                                onFilterValueChange={(id, vals) => setFilterValues(prev => ({ ...prev, [id]: vals }))}
                                                onQueryPatch={(childId, patch) => {
                                                    captureHistory();
                                                    setWidgets(prev => updateWidgetById(prev, childId, current => ({
                                                    ...current,
                                                    query: { ...current.query, ...patch }
                                                })));
                                                }}
                                                onLayoutChange={(nextLayout) => {
                                                    setWidgets(prev => updateWidgetById(prev, widget.id, current => {
                                                        const prevLayout = current.children?.layout || [];
                                                        if (layoutSignature(prevLayout) === layoutSignature(nextLayout)) return current;
                                                        return {
                                                            ...current,
                                                            children: { ...current.children, layout: nextLayout }
                                                        };
                                                    }));
                                                    setLayout(prev => {
                                                        const currentItem = prev.find(entry => entry.i === widget.id);
                                                        const nextH = Math.max(currentItem?.h || 6, containerHeightForInner(nextLayout, canvas.rowHeight));
                                                        if (!currentItem || currentItem.h === nextH) return prev;
                                                        return prev.map(entry => (
                                                            entry.i === widget.id ? { ...entry, h: nextH } : entry
                                                        ));
                                                    });
                                                }}
                                                onPatchChild={(childId, patch) => setWidgets(prev => updateWidgetById(prev, childId, current => ({
                                                    ...current,
                                                    ...patch
                                                })))}
                                                onExtractChild={handleExtractChild}
                                                onExtractDragChange={(active) => setExtractingFrom(active ? widget.id : null)}
                                            />
                                        ) : widget.type === 'text' ? (
                                        <textarea
                                            className="nested-text-editor"
                                            value={widget.content || ''}
                                            placeholder="Escribe el texto aquí…"
                                            onChange={e => setWidgets(prev => updateWidgetById(prev, widget.id, current => ({
                                                ...current,
                                                content: e.target.value
                                            })))}
                                            onClick={e => e.stopPropagation()}
                                            onMouseDown={e => e.stopPropagation()}
                                            onKeyDown={e => e.stopPropagation()}
                                            style={{
                                                ...getTextContentStyle(getWidgetStyle(widget), { editor: true })
                                            }}
                                        />
                                        ) : (
                                        <WidgetRenderer
                                            config={widget}
                                            engineFilters={engineFilters}
                                            showTitle={false}
                                            filterValues={filterValues}
                                            schemaColumns={schemasBySource[widget.query?.dataSourceId]?.columns || schemaColumns}
                                            schemasBySource={schemasBySource}
                                            filterDefs={filterDefs}
                                            dataSources={dataSources}
                                            joins={joins}
                                            exportable
                                            onQueryPatch={(patch) => {
                                                captureHistory();
                                                setWidgets(prev => updateWidgetById(prev, widget.id, current => ({
                                                ...current,
                                                query: { ...current.query, ...patch }
                                            })));
                                            }}
                                        />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </ResponsiveGridLayout>
                        </>
                    )}
                </div>
                </div>
                {propsOpen ? (
                <aside style={localStyles.sidebar}>
                    <div style={localStyles.sideHead}>
                        <h3 style={localStyles.sideTitle}>Propiedades</h3>
                        <button type="button" style={localStyles.hideBtn} onClick={() => setPropsOpen(false)} title="Ocultar propiedades">›</button>
                    </div>
                    <div className="creator-panel-scroll">
                    <div style={localStyles.canvasBox}>
                        <button type="button" style={localStyles.canvasToggle} onClick={() => setCanvasOpen(v => !v)}>
                            <span style={localStyles.sideSection}>Lienzo</span>
                            {!canvasOpen && (
                                <em style={localStyles.menuHint}>{canvas.columns} cols · {canvas.widthMode === 'px' ? `${canvas.width}px` : '100%'}</em>
                            )}
                            <span>{canvasOpen ? '▴' : '▾'}</span>
                        </button>
                        {canvasOpen && (
                        <>
                        <label style={localStyles.canvasField} title="Aplica a todo el reporte. «Ejecutivo»: acabado limpio; respeta los colores que hayas cambiado a mano.">
                            Tema del reporte
                            <select
                                value={reportTheme}
                                onChange={e => setReportTheme(normalizeReportTheme(e.target.value))}
                                style={localStyles.canvasInput}
                            >
                                {REPORT_THEMES.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </label>
                        <label style={localStyles.canvasField}>
                            Resolución
                            <input
                                type="number"
                                min={24}
                                max={96}
                                value={canvas.columns}
                                onChange={e => patchCanvasColumns(e.target.value)}
                                style={localStyles.canvasInput}
                            />
                        </label>
                        <label style={localStyles.canvasField}>
                            Ancho
                            <select
                                value={canvas.widthMode}
                                onChange={e => setCanvas(prev => ({ ...prev, widthMode: e.target.value }))}
                                style={localStyles.canvasInput}
                            >
                                <option value="percent">100%</option>
                                <option value="px">Pixeles</option>
                            </select>
                        </label>
                        {canvas.widthMode === 'px' && (
                            <label style={localStyles.canvasField}>
                                px
                                <input
                                    type="number"
                                    min={640}
                                    max={2400}
                                    value={canvas.width}
                                    onChange={e => setCanvas(prev => ({ ...prev, width: Number(e.target.value) || 1200 }))}
                                    style={localStyles.canvasInput}
                                />
                            </label>
                        )}
                        <label style={localStyles.canvasField}>
                            Alto
                            <input
                                type="number"
                                min={400}
                                max={4000}
                                value={canvas.height}
                                onChange={e => setCanvas(prev => ({ ...prev, height: Number(e.target.value) || 800 }))}
                                style={localStyles.canvasInput}
                            />
                        </label>
                        <label style={localStyles.canvasField}>
                            Alto de fila
                            <input
                                type="number"
                                min={8}
                                max={48}
                                value={canvas.rowHeight}
                                onChange={e => setCanvas(prev => ({ ...prev, rowHeight: Number(e.target.value) || 20 }))}
                                style={localStyles.canvasInput}
                            />
                        </label>
                        <label style={localStyles.canvasField}>
                            Fondo
                            <input
                                type="color"
                                value={canvas.background || DEFAULT_CANVAS.background}
                                onChange={e => setCanvas(prev => ({ ...prev, background: e.target.value }))}
                                style={{ width: '42px', height: '32px', border: 'none', padding: 0, background: 'transparent' }}
                            />
                        </label>
                        <ImageAssetField
                            compact
                            label="Imagen de fondo"
                            value={canvas.backgroundImage || ''}
                            onChange={(url) => setCanvas(prev => ({ ...prev, backgroundImage: url }))}
                        />
                        {canvas.backgroundImage ? (
                            <label style={localStyles.canvasField}>
                                Ajuste del fondo
                                <select
                                    value={canvas.backgroundFit || 'cover'}
                                    onChange={e => setCanvas(prev => ({ ...prev, backgroundFit: e.target.value }))}
                                    style={localStyles.canvasInput}
                                >
                                    <option value="cover">Cubrir</option>
                                    <option value="contain">Contener</option>
                                    <option value="repeat">Repetir</option>
                                </select>
                            </label>
                        ) : null}
                        </>
                        )}
                    </div>
                    <div style={localStyles.canvasBox}>
                        <button type="button" style={localStyles.canvasToggle} onClick={() => setElementOpen(v => !v)}>
                            <span style={localStyles.sideSection}>Elemento</span>
                            {!elementOpen && (
                                <em style={localStyles.menuHint}>{selectedWidget?.title || 'Ninguno seleccionado'}</em>
                            )}
                            <span>{elementOpen ? '▴' : '▾'}</span>
                        </button>
                        {elementOpen && (
                    <WidgetConfigurator
                        widget={selectedWidget}
                        knownNames={knownNames}
                        draggingField={draggingField}
                        onChange={handleWidgetChange}
                        onChangeSource={(widgetId, targetSourceId) => openReplaceDialog({
                            mode: 'widget',
                            widgetId,
                            sourceId: widgetSourceId(findReportWidget(widgetId), dataSources),
                            targetSourceId,
                        })}
                        heading="Elemento"
                        showHeading={false}
                        inputStyle={studio.controlCompact}
                        canvas={findParentWidget(widgets, selectedId)
                            ? { ...canvas, columns: INNER_CANVAS_COLUMNS }
                            : canvas}
                        layoutItem={findLayoutItem(widgets, layout, selectedId)}
                        schemaColumns={schemasBySource[selectedWidget?.query?.dataSourceId]?.columns || schemaColumns}
                        dataSources={dataSources}
                        dataSource={sourceRequestFields(selectedWidget?.query || activeSource || {}, { dataSources, joins })}
                        onLayoutItemChange={(patch) => {
                            captureHistory();
                            const parent = findParentWidget(widgets, selectedId);
                            if (parent) {
                                setWidgets(prev => updateWidgetById(prev, parent.id, current => ({
                                    ...current,
                                    children: {
                                        ...current.children,
                                        layout: (current.children?.layout || []).map(item => (
                                            item.i === selectedId ? { ...item, ...patch } : item
                                        ))
                                    }
                                })));
                                return;
                            }
                            setLayout(prev => prev.map(item => item.i === selectedId ? { ...item, ...patch } : item));
                        }}
                    />
                        )}
                    </div>
                    </div>
                </aside>
                ) : (
                    <button type="button" style={localStyles.rail} onClick={() => setPropsOpen(true)} title="Mostrar propiedades">
                        Propiedades
                    </button>
                )}
            </div>
            {shortcutsOpen && (
                <div style={localStyles.shortcutsScrim} onClick={() => setShortcutsOpen(false)}>
                    <div style={localStyles.shortcutsPanel} onClick={e => e.stopPropagation()} role="dialog" aria-label="Atajos de teclado">
                        <div style={localStyles.shortcutsHead}>
                            <strong>Atajos de teclado</strong>
                            <button type="button" style={localStyles.canvasBtn} onClick={() => setShortcutsOpen(false)}>Cerrar</button>
                        </div>
                        <table style={localStyles.shortcutsTable}>
                            <tbody>
                                {CREATOR_SHORTCUTS.map(([keys, action]) => (
                                    <tr key={keys}>
                                        <td style={localStyles.shortcutKeys}><kbd style={localStyles.kbd}>{keys}</kbd></td>
                                        <td style={localStyles.shortcutAction}>{action}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {previewOpen && (
                <div style={localStyles.previewScrim} onClick={() => setPreviewOpen(false)}>
                    <div style={localStyles.previewPanel} onClick={e => e.stopPropagation()}>
                        <div style={localStyles.previewBar}>
                            <strong>Vista previa</strong>
                            <span style={localStyles.canvasHint}>Así lo verá el usuario. Los Looker en iframe no se tocan.</span>
                            <button type="button" style={localStyles.canvasBtn} onClick={() => setPreviewOpen(false)}>Cerrar</button>
                        </div>
                        <div style={localStyles.previewBody}>
                            <CustomReportRenderer report={previewReport} useSnapshot={false} />
                        </div>
                    </div>
                </div>
            )}
        </div>
        </DashboardThemeContext.Provider>
    );
};

const localStyles = {
    workspace: { display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minHeight: 0, overflow: 'hidden' },
    menuBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: '#fff',
        border: '1px solid #dadce0',
        borderRadius: '10px',
        padding: '6px 8px',
        flexWrap: 'wrap',
        flexShrink: 0
    },
    menuTabs: { display: 'flex', gap: '4px', flex: 1, minWidth: 0, flexWrap: 'wrap' },
    menuTab: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        border: '1px solid transparent',
        background: 'transparent',
        borderRadius: '8px',
        padding: '6px 10px',
        cursor: 'pointer',
        color: '#3c4043',
        fontWeight: 700,
        fontSize: '0.82rem'
    },
    menuTabOn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        border: '1px solid #5E35B1',
        background: '#f3e5f5',
        borderRadius: '8px',
        padding: '6px 10px',
        cursor: 'pointer',
        color: '#4a148c',
        fontWeight: 700,
        fontSize: '0.82rem'
    },
    menuHint: { fontStyle: 'normal', fontWeight: 500, fontSize: '0.7rem', color: '#5f6368', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    menuCaret: { fontSize: '0.7rem', color: '#7b1fa2' },
    menuActions: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: 'auto' },
    menuPanel: {
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        background: '#fff',
        border: '1px solid #e0d4f7',
        borderRadius: '10px',
        padding: '10px',
        flexShrink: 0,
        maxHeight: '40%',
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain'
    },
    canvasToggle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        width: '100%',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        padding: 0,
        color: '#5E35B1'
    },
    toolbarTitle: {
        fontSize: '0.72rem',
        fontWeight: 700,
        color: '#5E35B1',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        alignSelf: 'center',
        minWidth: '64px'
    },
    sideHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px', flexShrink: 0 },
    sideTitle: { margin: 0, fontSize: '1rem', color: '#5E35B1' },
    sideSection: { fontSize: '0.72rem', fontWeight: 700, color: '#5E35B1', textTransform: 'uppercase', letterSpacing: '0.04em' },
    canvasBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '10px',
        background: '#faf7ff',
        border: '1px solid #e8def8',
        borderRadius: '10px',
        marginBottom: '12px'
    },
    hideBtn: {
        border: '1px solid #e0d4f7',
        background: '#faf7ff',
        color: '#5E35B1',
        borderRadius: '6px',
        width: '26px',
        height: '26px',
        cursor: 'pointer',
        fontSize: '1rem',
        lineHeight: 1
    },
    toolbar: {
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        background: '#fff',
        border: '1px solid #dadce0',
        borderRadius: '10px',
        padding: '8px 10px',
        flexShrink: 0
    },
    toolbarBlock: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, flex: 1 },
    error: {
        backgroundColor: '#fce8e6',
        color: '#c5221f',
        padding: '10px 12px',
        borderRadius: '8px',
        fontSize: '0.9rem'
    },
    missingBadge: {
        flexShrink: 0,
        padding: '1px 8px',
        borderRadius: 999,
        backgroundColor: '#fce8e6',
        color: '#c5221f',
        fontSize: '0.68rem',
        fontWeight: 700,
        whiteSpace: 'nowrap'
    },
    notice: {
        backgroundColor: '#f4eefc',
        color: '#4527a0',
        padding: '10px 12px',
        borderRadius: '8px',
        fontSize: '0.9rem'
    },
    body: { display: 'flex', gap: '8px', alignItems: 'stretch', flex: 1, minHeight: 0, overflow: 'hidden' },
    rail: {
        flexShrink: 0,
        width: '32px',
        border: '1px solid #dadce0',
        borderRadius: '12px',
        background: '#fff',
        color: '#5E35B1',
        cursor: 'pointer',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        writingMode: 'vertical-rl',
        transform: 'rotate(180deg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px 0'
    },
    canvas: {
        flex: 1,
        backgroundColor: '#f4f5f7',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#c5c9d0',
        borderRadius: 12,
        padding: 8,
        minWidth: 0,
        minHeight: 0,
        overflow: 'auto'
    },
    stageWell: {
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#dfe1e5',
        borderRadius: 14,
        padding: 14,
        overflowX: 'auto',
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        overscrollBehavior: 'contain',
        boxSizing: 'border-box',
    },
    artboard: {
        flex: 1,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#bdc1c6',
        borderRadius: 4,
        boxShadow: '0 1px 0 rgba(255,255,255,0.7) inset, 0 10px 32px rgba(28,29,33,0.14)',
        padding: 0,
        minWidth: 0,
        minHeight: 0,
        overflow: 'auto',
        boxSizing: 'border-box',
    },
    placeholder: {
        height: '100%',
        minHeight: '420px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: '#5f6368',
        padding: '40px'
    },
    sidebar: {
        width: '300px',
        flexShrink: 0,
        background: '#fff',
        border: '1px solid #dadce0',
        borderRadius: '12px',
        padding: '12px',
        overflow: 'hidden',
        minHeight: 0,
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column'
    },
    widgetCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#c4c7cc',
        boxShadow: '0 1px 3px rgba(28, 29, 33, 0.12)'
    },
    widgetChrome: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        backgroundColor: '#f8f9fa',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dadce0',
        cursor: 'move',
        flexShrink: 0
    },
    widgetChromeCompact: {
        padding: '2px 8px',
        backgroundColor: '#fff',
        borderBottom: '1px dashed #e0e0e0'
    },
    widgetTitle: { fontWeight: 600, fontSize: '0.85rem', color: '#202124', flex: 1 },
    widgetType: { fontSize: '0.7rem', color: '#5E35B1', textTransform: 'uppercase', letterSpacing: '0.04em' },
    dupBtn: {
        border: 'none',
        background: 'transparent',
        color: '#5E35B1',
        cursor: 'pointer',
        fontSize: '0.95rem',
        lineHeight: 1
    },
    canvasToolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '8px'
    },
    arrangeRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px'
    },
    canvasHint: { fontSize: '0.72rem', color: '#5f6368', flex: 1 },
    canvasBtn: {
        padding: '6px 10px',
        borderRadius: '6px',
        border: '1px solid #d1c4e9',
        background: '#fff',
        color: '#4a148c',
        cursor: 'pointer',
        fontSize: '0.78rem',
        fontWeight: 600
    },
    deleteBtn: {
        border: 'none',
        background: 'transparent',
        color: '#c5221f',
        cursor: 'pointer',
        fontSize: '1.1rem',
        lineHeight: 1
    },
    widgetBody: { flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' },
    blockCard: {
        boxShadow: 'inset 0 0 0 1px rgba(126, 87, 194, 0.25), 0 2px 8px rgba(94, 53, 177, 0.08)'
    },
    blockBody: {
        padding: 0,
        background: 'linear-gradient(180deg, rgba(243, 229, 245, 0.7), rgba(255,255,255,0.9))'
    },
    dropOverlay: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        zIndex: 5,
        background: 'rgba(255,255,255,0.72)'
    },
    dropHalfDim: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRight: '1px dashed #174ea6',
        color: '#174ea6',
        fontWeight: 700,
        fontSize: '0.85rem'
    },
    dropHalfMet: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#137333',
        fontWeight: 700,
        fontSize: '0.85rem'
    },
    dropHalfFilter: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#e65100',
        fontWeight: 700,
        fontSize: '0.85rem',
        borderLeft: '1px dashed #e65100'
    },
    canvasField: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.75rem', color: '#5f6368', fontWeight: 600 },
    canvasInput: { padding: '6px 8px', borderRadius: '6px', border: '1px solid #dadce0', width: '100%', boxSizing: 'border-box' },
    previewScrim: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.62)',
        zIndex: 40,
        display: 'flex',
        padding: '20px',
        boxSizing: 'border-box'
    },
    previewPanel: {
        flex: 1,
        backgroundColor: '#e8eaed',
        borderRadius: '14px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#9aa0a6',
        boxShadow: '0 16px 48px rgba(0,0,0,0.32)'
    },
    previewBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#c4c7cc',
        backgroundColor: '#ffffff',
        flexShrink: 0
    },
    previewBody: { flex: 1, minHeight: 0, overflow: 'auto', backgroundColor: '#e8eaed' },
    shortcutsScrim: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.45)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box'
    },
    shortcutsPanel: {
        backgroundColor: '#ffffff',
        borderRadius: 14,
        boxShadow: '0 18px 48px rgba(36, 22, 64, 0.24)',
        padding: '16px 18px',
        width: 'min(520px, calc(100vw - 32px))',
        maxHeight: 'calc(100vh - 80px)',
        overflow: 'auto',
    },
    shortcutsHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
    shortcutsTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' },
    shortcutKeys: { padding: '6px 12px 6px 0', whiteSpace: 'nowrap', verticalAlign: 'top' },
    shortcutAction: { padding: '6px 0', color: '#3c4043' },
    kbd: {
        display: 'inline-block',
        padding: '2px 7px',
        borderRadius: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#dadce0',
        backgroundColor: '#f8f9fa',
        fontFamily: 'inherit',
        fontSize: '0.78rem',
        color: '#202124',
    }
};

export default ReportCreatorWorkspace;
